import { Draft, produce } from 'immer';
import { Component, Element, h, State, Prop, Event, EventEmitter, Watch, Listen, Method } from '@stencil/core';
import { state } from '../../../../../store/catalog-store';
import type { ColumnApi, GridOptions, ICellRendererParams, IServerSideDatasource } from 'ag-grid-enterprise';
import CustomRenderer from '../../../../my-component/UI/grid/renderers/custom-renderer';
import CustomActionsRenderer from '../../../../my-component/UI/grid/renderers/custom-actions-renderer';
import Star16 from '@carbon/icons/es/star/16';
import List16 from '@carbon/icons/es/list/16';
import { SearchServiceDatasource, rowGroups } from '../../../../../udp-utilities/grid/data-source/SearchServiceDatasource';
import { convertToAgGridColumns } from '../../../../../udp-utilities/grid/convertToAgGridColumns';
import {
  getSelectedView,
  getAvailableViews,
  saveView,
  updateView,
  updateViewAndConfigurations,
  getAvailableGridActions,
  getPromotedLookupMethod,
  executeQueryAdHocAndMapLookups,
  executeQueryWithParameters,
  getApiCatalogAndProduct,
  getApiCatalogById,
  executeQueryAdHoc,
  getAllHotlists,
  createAHotlist,
  deleteAHotlist,
  addRecordToAHotlist,
  loadAHotlist,
  removeRecordFromHotlist,
  deleteSelectedView,
  getApiMethodInstance,
  getApiMethod,
} from '../../../../../udp-utilities/api-udp/apiUtils';
import { UdpGridColumnDefs, useTreeDatasource } from '../conversionFunctions/conversionFunctions';
import '../../../../my-component/ag-grid.css';
import EmailCellRenderer from '../renderers/email-cell-renderer';
import OpenFunctionCellRenderer from '../renderers/open-function-cell-renderer';
import HotListAddRenderer from '../renderers/hot-list-add-renderer';
import { SharedContext } from '../renderers/sharedContext';
import HoverContentCellRenderer from '../renderers/hover-content-cell-renderer';
import { GridStateTypeEnums } from './enums/gridEnums';
import { SearchObject, FilterDetail, HotListObject, ActionItem } from './interfaces/gridInterfaces';
import { transformSearchData } from '../../../../../udp-utilities/grid/data-converters/transformSearchData';
import { LicenseKey } from './license/licenseKey';
import { buildSearchPayload } from './ambient-template-functions/buildGroupedSearchPayload';
// import { FilterElement } from '../../advanced-search/advanced-search-grouped';
import * as localforage from 'localforage';
import '../../../../../global/app.css';
import Menu16 from '@carbon/icons/es/menu/16';
import close16 from '@carbon/icons/es/close/16';
// import OverflowMenuHorizontal16 from '@carbon/icons/es/overflow-menu--horizontal/16';
import DateTimeCellRenderer from '../renderers/date-time-cell-renderer';
import { RenderConfig } from './interfaces/gridInterfaces';
import ActionsRenderer from '../renderers/actions-renderer';
import { getActionColumnWidth, sortActionItemOrder, splitActionList } from '../../../../../udp-utilities/grid/action-renderers/action-column-util';
import { GridApi } from 'ag-grid-enterprise';
import { lazySetupAgGridLibrary } from '../../../../../global/ag-grid';
import { Memoize } from 'fast-typescript-memoize';
import { GridReadyEvent } from 'ag-grid-community';
import StatusRenderer from '../renderers/status-renderer';
import IconRenderer from '../renderers/icon-renderer';
import CurrencyRenderer from '../renderers/currency-renderer';
import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';

type NotificationStatus = 'error' | 'success' | 'warning';

@Component({
  tag: 'ambient-template-grid',
  styleUrl: './ambient-template-grid.css',
  shadow: true,
})
export class AmbientTemplateGrid {
  private prevPivotMode: boolean;
  //private dynamicFormRef: HTMLUdpDynamicFormElement;

  @Event() viewSaved: EventEmitter;
  @Event() externalEmitter: EventEmitter;
  @Element() el: HTMLElement;

  @Prop() columnDefs: any[] = [];
  @Prop() fullViewData: any = {};
  @Prop() productId: number = 0;
  @Prop() rowData: any[] = [];
  @Prop() viewListrowData: any[] = [];
  @Prop() gridState: any[] = [];
  @Prop() gridId: string = '';
  @Prop({ mutable: true }) apiCatalogId: string = '';
  @Prop() userId: string = '';
  @Prop() userPermissions: any[] = [];
  @Prop({ mutable: true }) apiMethodId: string = '';
  @Prop({ mutable: true }) lookupApiMethodId: string = '';
  @Prop({ mutable: true }) entityName: string = '';
  @Prop() menuItems: Array<any> = [];
  @Prop() gridBarTitle: string = '';
  @Prop() viewChips: Array<any> = [];
  @Prop() additionalFilterChips: Array<any> = [];
  @Prop() kpiValues: Array<any> = [];
  @Prop() tenantId: string = '';
  @Prop() showAdvancedSearchFeature: boolean = true;
  @Prop() showFilterColumnFeature: boolean = true;
  @Prop() queryId: string = '';
  @Prop() accessToken: string;
  @Prop() defaultSearchObject: Array<any> = [];
  @Prop() actionId: string = '';
  @Prop() cellClickHandler: (value: any, callback: any) => void;
  @Prop() cellClickHandlerX: (value: any, callback: any) => void;
  @Prop() viewActionClickHandler: (value: any, callback: any) => void;
  @Prop() renderConfigs: RenderConfig[] = [];
  // @Prop() externalPayload: any = {}; // check to see if it fixes the issue with adding an empty object
  @Prop() externalPayload: any = null;
  @Prop() dataEmitter: any = {};
  @Prop() dataEmitterRequest: any = {};
  @Prop() fitGrid: string = '';
  @Prop() hotListIdentifier: string = '';
  @Prop() searchKey: Object = {};
  @Prop({ mutable: true }) filterObject: any = [
    {
      pageNumber: 1,
      pageSize: 20,
      filterElements: [],
      orderElements: [],
      groupingType: '',
      groupProperty: [],
      groupOperationList: [],
      eagerLoad: false,
      logicalSearchOperator: 2,
      type: 'advanced',
    },
  ];
  @Prop({ mutable: true }) externalSearchFilter: Array<any> = [];
  @Prop() noResults: boolean = false;
  @Prop() maxChipsAllowed: number = 5;
  @Prop() suppressMenuHide: boolean = true; // default show column menu option without hovering
  @Prop() showRank: boolean = false;
  @Prop() showHotlistButton: boolean = false;
  @Prop() columnNames: { [key: string]: string } = {};
  @Prop() buttonTransform: string = '';
  @Prop() rowGrouping: Array<any> = [];
  @Prop() restrictBulkActionsPermissions: boolean = false;
  @Prop() restrictedGridFeatures: { [key: string]: string } = {};
  @Prop() triggerGridUpdate: boolean = false;
  @Prop() hideActionsColumn: boolean = false; //temporary, need better approach
  @Prop() primaryActionLabel: string;
  @Prop() primaryActionOnClick: (e: any) => void;
  @Prop() primaryActionIcon: string;
  @Prop() showInsightsAction: boolean = false;
  @Prop() insightsActionOnClick: (e: any) => void;
  @Prop() hideBulkActions: boolean = false;
  @Prop() enableCheckboxOnColumns: boolean = false;
  @Prop() hiddenColumnsList: Array<string> = [];
  @Prop() hideDefaultSearchFilterChips: boolean = false;
  @Prop() executeBulkAction: (actionId: string, data: any) => void;
  @Prop() bulkActionFunctions: Object = {};

  @State() drawerOpen: boolean = false;
  @State() filterPanelOpen: boolean = true;
  @State() saveDialogOpen: boolean = false;
  @State() editDialogOpen: boolean = false;
  @State() editViewConfirmationDialogOpen: boolean = false;
  @State() gridOptionsDrawerOpen: boolean = false;
  @State() viewListGridOptions: GridOptions;
  @State() filterChips: Array<any> = [];
  @State() promotedEntity: any = {};
  @State() isServerSide: boolean = true;
  @State() showNotification: boolean = false;
  @State() notificationMessage: string = '';
  @State() notificationStatus: NotificationStatus = 'success';
  @State() loadedView: Array<any> = [];
  @State() loadedViewTitle: string = '';
  @State() loadedViewData: string = '';
  @State() currentColumnOrdering: Array<any> = [];
  @State() currentHiddenColumns: Array<any> = [];
  @State() rowsSelected: boolean = false;
  @State() selectedRowCount: number = 0;
  @State() serverColumnDefs: Array<any> = [];
  @State() isSetupComplete: boolean = false;
  @State() extendedSearchFilterList: Array<any> = [];
  @State() viewListRowData: Array<any> = [];
  @State() bulkActions: Array<any> = [];
  @State() sheetContent: string = 'advancedSearch';
  @State() searchObject: SearchObject = { filterElements: [] };
  @State() configuredColumnDefs: Array<any> = [];
  @State() hasConfiguredColumnDefs: boolean = false;
  @State() fullDataColumns: any[] = [];
  @State() filterOn: string = '';
  @State() gridOptions: GridOptions;
  @State() agGrid:
    | undefined
    | {
        api: GridApi<any>;
        // @note As of ag-grid@v31 ColumnApi has been deprecated and all methods will move to api.
        columnApi: ColumnApi;
      };
  @State() hasSearchRank: boolean;
  @State() serverSideDatasource: IServerSideDatasource | undefined;
  @State() tempSearchObject: { searchField: string; searchOperator: string; searchValue: any; groupId: number } = {
    searchField: '',
    searchOperator: '',
    searchValue: '',
    groupId: 1,
  };
  @State() catalogList: any = {};
  @State() dataRowClicked: any = {};
  @State() advancedSearchFilterList: Array<any> = [];
  @State() allHotlists: Array<any> = [];
  @State() externalToggle: string = '';
  @State() viewId: string = '';
  @State() defaultFilterObject: Array<any> = [
    {
      pageNumber: 1,
      pageSize: 20,
      filterElements: [
        //   {
        //     "searchField": "genderCode",
        //     "searchOperator": "=",
        //     "searchValue" : "1"
        // }
      ],
      orderElements: [],
      groupingType: '',
      groupProperty: [],
      groupOperationList: [],
      eagerLoad: false,
      logicalSearchOperator: 1,
      type: 'advanced',
    },
  ];

  @State() filterRenderList: Array<any> = [
    {
      pageNumber: 1,
      pageSize: 20,
      filterElements: [
        //   {
        //     "searchField": "Title",
        //     "searchOperator": "IN",
        //     "searchValue": "VP"
        // }
      ],
      orderElements: [],
      groupingType: '',
      groupProperty: [],
      groupOperationList: [],
      eagerLoad: false,
      logicalSearchOperator: 1,
      type: 'advanced',
    },
  ];
  @State() logicalSearchOperator: number = 1;
  @State() lookupsMap: any = null;
  @State() formattedBulkActions: { label: string; value: string }[] = [];
  @State() advancedDrawerOpen: boolean = false;
  @State() viewDrawerOpen: boolean = false;
  @State() hotlistDrawerOpen: boolean = false;
  @State() customSearchDrawerOpen: boolean = false;
  @State() businessObjectKey: string;
  @State() hotListActive: string;
  @State() hotListCount: number = 0;
  @State() hotListId: string;
  @State() loadedHotlistTitle: string = '';
  @State() showHotlistForm: boolean = false;
  @State() confirmDialog: boolean = false;
  @State() confirmDialogDeleteView: boolean = false;
  @State() filteredCount: Array<any> = [];
  @State() hotlistSelectedFull: object = {};
  @State() advancedGroupSearchNodeId: string = '';
  @State() otherId: string = '';
  @State() updateForConfiguredColumnDefs: boolean = true;
  @State() filterObjectsWithGroups: Array<any> = [
    {
      id: 'root',
      logicalOperator: 'AND',
      filters: [],
    },
  ];
  @State() loadedGridView: object = {};
  @State() linearLoading: boolean = false;
  @State() selectedGridId: string = '';
  @State() selectedViewName: string = '';
  @State() selectedViewIsDefault: boolean = false;
  @State() selectedViewGridViewVisibilityTypeId: number;
  @State() selectedGridViewGridConfigurations: any[];
  @State() allFilters: Array<any> = [];
  @State() visibleChips: any[];
  @State() overflowChips: any[];
  @State() overflowActive: boolean = false;
  @State() setSearchStart: boolean = false;
  @State() refreshChips: boolean = false;
  @State() gridUID: string = '';
  @State() recentSearches: any[] = [];
  @State() recentSearchesOptions: any[] = [];
  @State() isRecentSearch: boolean = false;
  @State() gridError: boolean = false;
  @State() gridHeight: string = 'calc(100vh - 200px)';
  @State() openActionPopoverMenu: boolean = false;
  @State() selectedRowParams: ICellRendererParams;
  @State() actionMenuAnchorElement: HTMLElement;
  @State() overflowActionMenuItems: ActionItem[];
  @State() initialFilterData: any = {
    filterObjectsWithGroups: [
      {
        id: 'root',
        logicalOperator: 'AND',
        filters: [],
      },
    ],
    logicalSearchOperator: 1,
    filterObject: [
      {
        pageNumber: 1,
        pageSize: 20,
        filterElements: [],
        orderElements: [],
        groupingType: '',
        groupProperty: [],
        groupOperationList: [],
        eagerLoad: false,
        logicalSearchOperator: 2,
        type: 'advanced',
      },
    ],
    filterRenderList: [
      {
        pageNumber: 1,
        pageSize: 20,
        filterElements: [],
        orderElements: [],
        groupingType: '',
        groupProperty: [],
        groupOperationList: [],
        eagerLoad: false,
        logicalSearchOperator: 1,
        type: 'advanced',
      },
    ],
  };
  @State() defaultColumnState: any[] = null;
  viewListcolumnDefs: any[];

  @Watch('triggerGridUpdate')
  handleTriggerGridUpdate(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue && this.agGrid) {
      this.agGrid?.api?.refreshServerSide({ purge: true });
      this.updateServerSideDatasource('', 'advanced search sheet');
    }
  }

  @Event() selectedIdsChanged: EventEmitter<number[]>;

  @Listen('noDataDetected', { target: 'document' })
  noDataHandler() {
    this.linearLoading = false;
    this.noResults = true;
  }

  @Watch('accessToken')
  accessTokenChanged(newValue: string) {
    if (newValue) {
      localStorage.setItem('accessToken', newValue);
    }
  }

  @Watch('catalogList')
  watchHandler(newVal: any, _oldVal: any) {
    if (newVal) {
      this.filterByCatalogObjectName(this.entityName);
    }
  }

  @Memoize()
  async getApiCatalogId(): Promise<string> {
    if (this.apiCatalogId) return this.apiCatalogId;

    const fetchedData = await getApiCatalogAndProduct(this.tenantId, this.productId, this.accessToken);
    return (this.apiCatalogId = fetchedData.apiCatalogId);
  }

  async getApiMethodId(): Promise<string> {
    if (this.apiMethodId) return this.apiMethodId;

    const apiMethodInstance = await this.getApiMethodInstance();
    const apiMethodId = apiMethodInstance.apiMethodId;
    if (!apiMethodId) {
      throw new Error('apiMethodId not found');
    }

    return (this.apiMethodId = apiMethodId);
  }

  async getLookupApiMethod(): Promise<string> {
    if (this.lookupApiMethodId) return this.lookupApiMethodId;

    const promotedLookupMethod = await this.getPromotedLookupMethod();
    return (this.lookupApiMethodId = promotedLookupMethod?.unityBaseGetDataMethod?.apiMethodId);
  }

  @Memoize()
  async getApiMethodInstance() {
    return await getApiMethodInstance(this.queryId, this.tenantId, this.accessToken);
  }

  @Memoize()
  async getApiMethod() {
    return await getApiMethod(await this.getApiMethodId(), this.accessToken);
  }

  @Memoize()
  async getPromotedLookupMethod() {
    return await getPromotedLookupMethod(await this.getApiCatalogId(), this.productId, this.accessToken);
  }

  @Memoize()
  async getFilterBody() {
    const apiMethodInstance = await this.getApiMethodInstance();
    return apiMethodInstance.body;
  }

  /**
   * Get column definitions from props or API Method Instance catalogModification
   */
  async getColumnDefinitions(): Promise<any[]> {
    if (this.columnDefs && this.columnDefs.length > 0) return this.columnDefs;

    const apiMethodInstance = await this.getApiMethodInstance();
    return JSON.parse(apiMethodInstance.catalogModification);
  }

  async getEntityName() {
    if (this.entityName) return this.entityName;

    const apiMethod = await this.getApiMethod();
    return (this.entityName = apiMethod.entityName);
  }

  updateFilterObject(recipe: (state: Draft<any[]>) => any[] | void | undefined) {
    this.filterObject = produce(this.filterObject, recipe);
  }

  async filterByCatalogObjectName(catalogObjectName: string) {
    if (!this.catalogList) {
      console.warn('catalogList is empty or undefined.');
      return;
    }
    // change to list from UdpGridColumnDefs
    const catalogObjectList = this.catalogList?.ctlg?.catalogObjectList;
    if (Array.isArray(catalogObjectList)) {
      catalogObjectList.forEach(object => {
        if (object.catalogObjectName === catalogObjectName) {
          const names = object.properties.map(property => {
            return { name: property.name };
          });
          this.advancedSearchFilterList = names;
        }
      });
    } else {
      console.warn('catalogObjectList is not an array or is undefined.');
    }
  }

  // PUTS apiCatalogData into localStorage
  getExtendedAdvancedSearchFilterList = async () => {
    const accessToken = this.accessToken;

    try {
      const apiCatalogId = await this.getApiCatalogId();
      let apiCatalogData: any;

      // Retrieve apiCatalogData from localForage or make the API call if not found
      apiCatalogData = await localforage.getItem('apiCatalogData');
      if (!apiCatalogData) {
        // Fetch data from the API if not available in localForage
        apiCatalogData = await getApiCatalogById(apiCatalogId, accessToken);
        // Store the fetched data in localForage for future use
        await localforage.setItem('apiCatalogData', apiCatalogData);
      }

      const transformedData = useTreeDatasource(apiCatalogData, await this.getEntityName(), true, true, '', '', '');

      if (transformedData) {
        state.catalogData = apiCatalogData;
        this.extendedSearchFilterList = transformedData;
      } else {
        console.error('No data returned from useTreeDatasource');
      }
    } catch (error) {
      this.gridError = true;
      console.error('Error fetching API Catalog:', error);
    }
  };

  constructor() {
    this.changeOperator = this.changeOperator?.bind(this);
  }
  changeOperator(event: CustomEvent) {
    // Convert string to integer
    const convertedValue = parseInt(event.detail, 10);
    // Update the state
    this.logicalSearchOperator = convertedValue;
  }

  @Method()
  async updateFilterAndFetch(newFilter: Array<any>) {
    this.updateFilterObject(() => [
      {
        pageNumber: 1,
        pageSize: 20,
        filterElements: [...newFilter],
        orderElements: [],
        groupingType: '',
        groupProperty: [],
        groupOperationList: [],
        eagerLoad: false,
        logicalSearchOperator: 2,
        type: 'advanced',
      },
    ]);
    // Now call updateServerSideDatasource
    this.updateServerSideDatasource('', 'updateFilterAndFetch');
  }

  buildSearchFilterElements(filterElement, forObject, isInitialFilterElement) {
    const elementId = `id-${Math.random().toString(36).substr(2, 9)}`;
    let filterElementObject = {
      fieldId: elementId,
      groupId: 1,
      isInitialFilterElement: isInitialFilterElement,
      originalSearchOperator: filterElement.searchOperator,
      originalSearchValue: filterElement.searchValue,
      ...filterElement,
    };

    if (forObject) {
      filterElementObject = {
        id: uuid(),
        otherId: elementId,
        level: 0,
        ...filterElementObject,
      };
    }

    return filterElementObject;
  }

  @Watch('externalSearchFilter')
  @Watch('defaultSearchObject')
  async handleFilteringSetup() {
    const filterBodyStr = await this.getFilterBody();
    const filterBody = !!filterBodyStr && typeof filterBodyStr === 'string' ? JSON.parse(filterBodyStr) : {};
    const intialFilterElements = filterBody?.filterElements || [];
    const updatedInitialFilterElements = [];
    const updatedInitialFilterElementsForObject = [];
    intialFilterElements.forEach(filterElement => {
      const elementId = `id-${Math.random().toString(36).substr(2, 9)}`;
      updatedInitialFilterElements.push({ id: uuid(), fieldId: elementId, otherId: elementId, level: 0, groupId: 1, isInitialFilterElement: true, ...filterElement });
      updatedInitialFilterElementsForObject.push({ fieldId: elementId, groupId: 1, isInitialFilterElement: true, ...filterElement });
    });

    // construct filterElements from defaultSearchObject props
    this.defaultSearchObject.forEach(searchObj => {
      updatedInitialFilterElements.push(this.buildSearchFilterElements(searchObj, true, true));
      updatedInitialFilterElementsForObject.push(this.buildSearchFilterElements(searchObj, false, true));
    });

    // construct filterElements from filterObject props
    this.externalSearchFilter.forEach(filterElement => {
      updatedInitialFilterElements.push(this.buildSearchFilterElements(filterElement, true, false));
      updatedInitialFilterElementsForObject.push(this.buildSearchFilterElements(filterElement, false, false));
    });

    this.filterObjectsWithGroups = [
      {
        id: 'root',
        logicalOperator: filterBody?.logicalSearchOperator === 2 ? 'OR' : 'AND',
        filters: updatedInitialFilterElements,
      },
    ];

    this.initialFilterData.filterObjectsWithGroups = cloneDeep(this.filterObjectsWithGroups);

    this.logicalSearchOperator = filterBody?.logicalSearchOperator || 1;
    this.initialFilterData.logicalSearchOperator = filterBody?.logicalSearchOperator || 1;

    this.updateFilterObject(filterObject => {
      filterObject[0].filterElements = [...updatedInitialFilterElementsForObject];
    });
    const initialFilterObject = cloneDeep(this.initialFilterData.filterObject);
    initialFilterObject[0].filterElements = [...updatedInitialFilterElementsForObject];

    this.initialFilterData.filterObject = initialFilterObject;

    this.filterRenderList = cloneDeep(this.filterObject);
    this.initialFilterData.filterRenderList = cloneDeep(this.initialFilterData.filterObject);

    this.updateChipLayout();
  }

  async componentWillLoad() {
    this.gridError = false;
    if (this.accessToken) {
      localStorage.setItem('accessToken', this.accessToken);
    }

    localStorage.setItem('tenantId', this.tenantId);
    localStorage.setItem('productId', `${this.productId}`);

    this.gridUID = this.gridId + '-' + this.queryId;

    this.handleAdditionalSetup();
    await this.handleFilteringSetup();

    this.viewListcolumnDefs = [
      {
        headerName: 'Name',
        field: 'name',
        resizable: true, // Enable resizing
        height: 100,
      },
      {
        field: 'gridViewVisibilityTypeId',
        cellRenderer: CustomRenderer,
        height: '100px',
      },
      {
        headerName: 'Actions',
        field: 'gridViewId',
        cellRenderer: CustomActionsRenderer,
        cellRendererParams: {
          onClick: this.handleButtonClick,
        },
      },
    ];

    // Preload the ag-Grid library
    lazySetupAgGridLibrary({ LicenseKey });

    // Grab list of recent searches from local storage
    this.getRecentSearches();
  }
  // End of ComponentWillLoad

  waitForGrid(callback: () => void) {
    const observer = new MutationObserver(([], observer) => {
      const myGrid = this.el.shadowRoot.querySelector('#myGrid');
      if (myGrid) {
        observer.disconnect(); // Stop observing once the grid is found
        callback();
      }
    });

    observer.observe(this.el.shadowRoot, {
      childList: true, // Observe direct children changes
      subtree: true, // Observe nested children changes
    });
  }

  componentDidLoad() {
    lazySetupAgGridLibrary({ LicenseKey })
      .then(() => {
        // Initialize the ag-Grid
        this.waitForGrid(() => this.initializeGrid());
      })
      .catch(error => {
        this.gridError = true;
        console.error('Error during grid initialization:', error);
      });

    SharedContext.myClickCallback = this.cellClickHandler?.bind(this);
    SharedContext.viewActionCallback = this.viewActionClickHandler?.bind(this);
    SharedContext.thirdActionCallback = this.cellClickHandlerX?.bind(this);
    SharedContext.hotListAddHandler = (otherCellValue, callbackId) => {
      this.businessObjectKey = otherCellValue;
      console.log(callbackId);
    };

    // Can handle add or delete hotlist record from a list
    SharedContext.hotListAddHandler = (otherCellValue, callbackId) => {
      console.log(callbackId);

      function findMatchingHotListId(dataObject: any, otherCellValue: any): string | null {
        for (const record of dataObject.records) {
          const parsedData = JSON.parse(record.data);

          if (parsedData.businessObjectKey === otherCellValue.businessObjectKey) {
            return record.hotListRecordId;
          }
        }
        return null;
      }

      this.businessObjectKey = otherCellValue;
      if (!this.hotListActive) {
        this.hotlistDrawerOpen = true;
      } else {
        const matchingHotListId = findMatchingHotListId(this.hotlistSelectedFull, otherCellValue);
        removeRecordFromHotlist(matchingHotListId, this.tenantId, this.accessToken);
        const hotlistSelectedFull: HotListObject = this.hotlistSelectedFull as HotListObject;
        this.removeFilterSelection({
          searchField: 'contactId',
          searchValue: matchingHotListId,
        });
        this.handleRequestHotlist(hotlistSelectedFull.hotListId);
      }
    };

    this.updateChipLayout();
    window.addEventListener('resize', this.updateChipLayout);
  }
  // End of ComponentDidLoad

  handleLinerLoading = (isLoading: boolean) => {
    this.linearLoading = isLoading;
  };

  //#region Action Renderer
  updateActionRendererState = (event: any, params: any) => {
    this.openActionPopoverMenu = !this.openActionPopoverMenu;
    this.selectedRowParams = params;
    this.actionMenuAnchorElement = event.currentTarget;
  };

  handleOnCloseActionPopover = event => {
    event?.preventDefault();
    this.selectedRowParams = null;
    this.openActionPopoverMenu = false;
    this.actionMenuAnchorElement = null;
  };

  getOverflowActionList = (overflowActions, params) => {
    return overflowActions?.map((action: ActionItem) => {
      const disabledAction = action?.checkDisabled ? action?.checkDisabled(params) : action?.disabled ?? false;
      const actionTitle = action?.getTitle ? action?.getTitle(params) : action?.title;
      return (
        <udp-menu-item
          label={actionTitle}
          iconName={action?.iconName}
          disabled={disabledAction}
          handleOnClick={e => {
            action?.handleOnClick(e, params);
          }}
        />
      );
    });
  };
  //#endregion

  // Main search call to server (old)
  updateServerSideDatasource(requestType: string, specialType: string) {
    console.warn('(dead branch) updateServerSideDataSouced: ', specialType);
    // Optionally close the drawer.

    if (requestType != 'remove') {
      this.drawerOpen = false;
    }

    if (requestType != 'remove') {
      this.advancedDrawerOpen = false;
    }
  }
  //#region Column Defs
  @Watch('configuredColumnDefs')
  @Watch('columnNames')
  @Watch('bulkActions')
  changeServerColumnDefs() {
    const updateColumnDefs = serverColumnDefs => {
      // Create a Set of fields from fullDataColumns for quick lookup
      // const fullDataFieldsSet = new Set(this.fullDataColumns.map(column => column.field));

      // Start with serverColumnDefs and update hide property
      let updatedColumns = serverColumnDefs.map(columnDef => {
        // Check if the field is 'searchRank' or in fullDataFieldsSet

        const hide = false;
        return {
          ...columnDef,
          hide: hide,
        };
      });

      // Add any columns from fullDataColumns that are not in serverColumnDefs
      this.fullDataColumns.forEach(column => {
        if (!serverColumnDefs.some(serverColumn => serverColumn.field === column.field) && this.showRank) {
          const hide = column.field !== 'searchRank';
          updatedColumns.push({ ...column, hide: hide });
        }
      });

      // Update the column header names
      if (this.columnNames) {
        const updatedColumnsWithNewNames = updatedColumns.map(obj => ({
          ...obj,
          headerName: this.columnNames[obj.headerName] || obj.headerName, // Use dictionary value or fallback to original value
        }));

        // Update the state or the updatedColumns with the new names
        updatedColumns = updatedColumnsWithNewNames;
      }

      // Hide column
      if (rowGroups.length > 0 && rowGroups) {
        let groupProps = null;
        groupProps = rowGroups.map(prop => prop.toLowerCase());
        updatedColumns = updatedColumns.map(column => {
          // Check if this column's field is present in groupProperty
          const hide = groupProps.includes(column.field.toLowerCase());
          return {
            ...column,
            hide: hide,
          };
        });
      }

      return updatedColumns;
    };

    const displayColumns = updateColumnDefs(this.configuredColumnDefs);

    const modifiedColumnDefs = displayColumns.map(column => {
      // Check if this column's headerName is present in displayColumns
      const isColumnDisplayed = displayColumns.find(displayColumn => displayColumn.headerName === column.headerName);
      // If it's not present, add the 'hide' property
      if (!isColumnDisplayed) {
        if (column.headerName === 'Search Rank') {
          return { ...column, sortable: false, hide: this.hasSearchRank ? false : true };
        } else {
          return { ...column, hide: true };
        }
      }
      // If it is present, return the column as is
      return column;
    });

    const addRenderers = (columnDefs, renderConfigs) => {
      return columnDefs.map(colDef => {
        // Find a matching configuration for this column
        const config = renderConfigs.find(cfg => cfg.field === colDef.field);
        if (config) {
          // Merge existing cellRendererParams with ones from config, prioritizing config params
          const cellRendererParams = {
            ...colDef.cellRendererParams,
            ...config.cellRendererParams,
            otherField: config.otherField, // Assuming you might want to keep using this pattern
            callbackId: config.callbackId,
            showViewAction: true,
            showThirdAction:
              this.showHotlistButton &&
              !(this.restrictedGridFeatures?.Hotlist && !this.userPermissions.some(permission => permission.name === this.restrictedGridFeatures.Hotlist)),
          };

          if (config?.rendererName === 'actionsRenderer') {
            const maximumVisibleActions = 4;
            //set minWidth and maxWidth for action columns
            const actionColumnWidth = getActionColumnWidth(config?.cellRendererParams?.actionList);
            colDef.minWidth = actionColumnWidth;
            colDef.maxWidth = actionColumnWidth;

            //sort action list
            const sortedActionList = sortActionItemOrder([...config.cellRendererParams.actionList]);

            if (config?.cellRendererParams?.actionList?.length > maximumVisibleActions) {
              // Split the action list if there are more than 4 actions
              const { visibleActions, overflowActions } = splitActionList(sortedActionList, maximumVisibleActions - 1);
              this.overflowActionMenuItems = overflowActions;
              visibleActions.push({
                id: 100,
                iconName: 'overflowMenuVertical',
                title: 'View More',
                handleOnClick: this.updateActionRendererState.bind(this),
                disabled: false,
              });

              cellRendererParams.actionList = visibleActions;
            }
          }

          if (config.rendererName === 'currencyRenderer') {
            colDef.type = 'rightAligned';
          }

          return {
            ...colDef,
            cellRenderer: config.rendererName,
            cellRendererParams: cellRendererParams,
          };
        }
        return colDef; // Return original colDef if no matching config is found
      });
    };

    const addActionsColumn = columnDefs => {
      const actionsColumnExists = columnDefs.some(column => column.field === 'actions');
      if (!actionsColumnExists && !this.hideActionsColumn) {
        const actionsColumn = {
          headerName: 'Actions',
          field: 'actions',
          cellRendererParams: {
            shouldDisplayCustomContent: false,
          },
          cellRenderer: 'hotListAddRenderer',
          pinned: 'right',
        };
        columnDefs.push(actionsColumn);
      }
      return columnDefs;
    };

    const addCheckboxColumn = columnDefs => {
      var bulkActions = this.bulkActions; // check if bulk actions are configured, if not then skip adding checkboxes
      if (bulkActions?.length > 0 || this.enableCheckboxOnColumns) {
        const checkboxColumnExists = columnDefs.some(column => column.field === 'checkbox');
        if (!checkboxColumnExists) {
          const checkboxColumn = {
            headerName: '', // No title for the checkbox column
            field: 'checkbox',
            checkboxSelection: true, // This enables the checkbox selection feature
            headerCheckboxSelection: false, // set to false now as the server-size for select all is not fully implemented
            pinned: 'left', // Pinned to the left
            lockVisible: true, // This prevents the column from being hideable
            maxWidth: 70,
            filter: false, // no need to show filter for this column
            menuTabs: [], // Disables the menu in the column header
            sortable: false, // Disables sorting for this column
          };
          columnDefs.unshift(checkboxColumn); // Use unshift to add it at the start of the array
        }
      }
      return columnDefs;
    };

    const addToolTip = columnDefs => {
      return columnDefs.map(colDef => {
        colDef.tooltipField = colDef.field;
        colDef.headerTooltip = colDef.headerName;
        return { ...colDef };
      });
    };

    // Usage of the function to add checkbox column
    const columnDefsWithCheckbox = addCheckboxColumn(modifiedColumnDefs);

    // Add the Actions column to the definitions with checkbox
    let columnDefsWithActions = addActionsColumn(columnDefsWithCheckbox);

    // Reorder the columns based on the currentColumnOrdering
    if (this.currentColumnOrdering.length > 0) {
      const reorderedColumns = this.currentColumnOrdering.map(currentColOrder => columnDefsWithActions.find(col => col.field === currentColOrder.colId));
      // Update the columnDefs with the reordered columns
      columnDefsWithActions = reorderedColumns;
    }

    // Update each column definition's 'hide' property with the value from grid state
    if (this.currentHiddenColumns?.length > 0) {
      columnDefsWithActions = columnDefsWithActions.map(colDef => {
        const hiddenCol = this.currentHiddenColumns.find(hiddenCol => hiddenCol.colId === colDef.field);
        if (hiddenCol) {
          return { ...colDef, hide: hiddenCol.hide };
        }
        return colDef;
      });
    }

    const columnDefsWithToolTips = addToolTip(columnDefsWithActions);

    // Now pass this updated array to addRenderers or other manipulation functions
    const columnDefsWithRenderers = addRenderers(columnDefsWithToolTips, this.renderConfigs);

    this.serverColumnDefs = columnDefsWithRenderers;
  }

  @Watch('agGrid')
  @Watch('serverColumnDefs')
  watchServerColumnDefs() {
    if (this.agGrid && this.serverColumnDefs.length > 0) {
      const baseColumnDefs = this.serverColumnDefs.map(colDef => {
        const shouldHide = this.hiddenColumnsList.includes(colDef.field);
        return {
          ...colDef,
          hide: shouldHide,
        };
      });

      this.agGrid.api.setColumnDefs(baseColumnDefs);
      this.defaultColumnState = this.agGrid.columnApi.getColumnState();
    }
  }

  @Watch('hiddenColumnsList')
  watchHiddenColumnList() {
    if (this.agGrid) {
      const newDefaultState = this.defaultColumnState.map(col => {
        const shouldHide = this.hiddenColumnsList.includes(col.colId);
        return {
          ...col,
          hide: shouldHide,
        };
      });
      this.defaultColumnState = newDefaultState;
      //TODO here might not want to apply column state if a view is active
      if (this.loadedView.length === 0) {
        this.agGrid.columnApi.applyColumnState({ state: this.defaultColumnState, applyOrder: true });
      }
    }
  }

  @Watch('configuredColumnDefs')
  configuredColumnDefsChanged(newValue: Array<any>) {
    this.hasConfiguredColumnDefs = newValue.length > 0;
  }
  //#endregion

  @Watch('isServerSide')
  @Watch('apiMethodId')
  @Watch('agGrid')
  @Watch('filterObject')
  @Watch('logicalSearchOperator')
  @Watch('lookupsMap')
  // @Watch('accessToken')
  @Watch('tenantId')
  changeServerSideDatasource(_newValue?: any, _oldValue?: any, propName?: string) {
    console.log(`changeServerSideDatasource triggered by ${propName || 'unknown'}`);
    this.linearLoading = true;

    // Ensure all prerequisites are met before attempting to update the data source.
    if (!this.isServerSide || !this.apiMethodId) {
      this.linearLoading = false;
      console.warn('Grid update prerequisites not met..');
      return;
    }

    this.noResults = false;

    let datasource = SearchServiceDatasource(
      null,
      this.gridUID,
      this.isRecentSearch,
      this.apiMethodId,
      this.hotListActive && this.extractContactIDs(this.hotlistSelectedFull).length === 0,
      this.filterObject,
      this.logicalSearchOperator,
      this.accessToken,
      this.tenantId,
      this.getLookupApiMethod(),
      executeQueryAdHocAndMapLookups,
      this.lookupsMap,
      this.defaultSearchObject,
      (dataFetched, totalCount) => {
        try {
          if (totalCount === 0) {
            this.linearLoading = false;
          }

          // Convert the fetched data to ag-grid columns, representing the full set of data columns
          this.fullDataColumns = convertToAgGridColumns(dataFetched);

          this.hasSearchRank = !!dataFetched?.[0]?.searchRank;

          if (this.agGrid) {
            // Assuming api is initialized

            // Listen for any event that signifies data has been updated or loaded
            this.agGrid.api.addEventListener('modelUpdated', () => {
              const rowCount = this.agGrid.api.getDisplayedRowCount();

              this.getExtendedAdvancedSearchFilterList();

              // Check if the grid has no rows displayed after a data update
              if (rowCount === 0) {
                // No rows in the grid, meaning the data source returned an empty pageList
                this.linearLoading = false; // Stop the loading animation
                // Optionally, trigger any other UI updates to reflect the empty state
              } else {
                // Rows are present, data loading was successful with content
                // Any additional handling for successful data load with rows
              }
            });
          }

          if (this.agGrid) {
            this.linearLoading = false;
            // load ctlg for advanced search after setting column defs
          }
        } catch (error) {
          this.gridError = true;
          console.error('Error processing data fetched for the grid:', error);
        }

        // Update list of recent searches
        this.getRecentSearches();
        this.isRecentSearch = false;
      },
      this.handleLinerLoading,
    );

    // SearchServiceDatasource end

    // Set the new data source and trigger a refresh.
    this.serverSideDatasource = datasource;

    this.getGridState();
  }

  @Watch('serverSideDatasource')
  @Watch('agGrid')
  setServerSideDatasource() {
    // Set the new data source and trigger a refresh.
    if (this.agGrid) {
      try {
        this.agGrid.api.setServerSideDatasource(this.serverSideDatasource);

        // No need to call refreshServerSide() as setting the datasource will implicitly refresh it.
      } catch (error) {
        this.gridError = true;
        console.error('Error setting server-side datasource:', error);
      }
    } else {
      this.serverSideDatasource.getRows({
        api: undefined,
        columnApi: undefined,
        context: undefined,
        request: {
          startRow: 0,
          endRow: 20,
          rowGroupCols: [],
          valueCols: [],
          pivotCols: [],
          pivotMode: false,
          groupKeys: [],
          filterModel: {},
          sortModel: [],
          // @ts-expect-error -- Prefetch property is custom
          prefetch: true,
        },
        fail() {},
        success() {},
      });
    }
  }

  adjustGridScroll() {
    const gridViewport = this.el.querySelector('.ag-center-cols-viewport') as HTMLElement;
    if (gridViewport && gridViewport.style) {
      gridViewport.style.overflowY = 'hidden';
    }
    const scrollViewport = this.el.querySelector('.ag-body-viewport.ag-layout-normal') as HTMLElement;
    if (scrollViewport && scrollViewport.style) {
      scrollViewport.style.overflowY = 'hidden';
    }
  }

  triggerResizeEvent() {
    let resizeEvent = new CustomEvent('resize');
    window.dispatchEvent(resizeEvent);
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.updateChipLayout();
  }

  @Watch('serverColumnDefs')
  serverColumnDefsChanged(newValue: any[]) {
    if (newValue && newValue.length != 0 && !this.isSetupComplete) {
      this.handleSecondarySetup();
      this.isSetupComplete = true;
    }
    this.updateChipLayout();
  }

  @Watch('filterRenderList')
  watchChipLayoutChanges() {
    this.updateChipLayout();
  }

  initializeGrid() {
    this.gridOptions = {
      animateRows: true,
      ...(this.isServerSide ? { rowModelType: 'serverSide' } : {}),
      ...(this.isServerSide ? { serverSideStoreType: 'partial' } : {}),
      ...(this.isServerSide ? { cacheOverflowSize: 2 } : {}),
      ...(this.isServerSide ? { cacheBlockSize: 20 } : {}),
      ...(this.isServerSide ? { maxConcurrentDatasourceRequests: 1 } : {}),
      pagination: true,
      paginationPageSize: 20,
      rowHeight: 32,
      defaultColDef: {
        filter: true,
        resizable: true,
      },
      rowSelection: 'multiple',

      suppressRowClickSelection: true,
      onSelectionChanged: () => {
        const selectedNodes = this.agGrid.api.getSelectedNodes();
        const rowCount = selectedNodes.length;
        this.rowsSelected = rowCount > 0;
        this.selectedRowCount = rowCount;
      },
      onCellClicked: event => {
        this.dataRowClicked = event.data;
      },

      // frameworkComponents: {
      //   emailCellRenderer: 'email-cell-renderer',
      // },
      components: {
        emailCellRenderer: EmailCellRenderer,
        openFunctionCellRenderer: OpenFunctionCellRenderer,
        hoverContentCellRenderer: HoverContentCellRenderer,
        hotListAddRenderer: HotListAddRenderer,
        dateTimeCellRenderer: DateTimeCellRenderer,
        actionsRenderer: ActionsRenderer,
        statusRenderer: StatusRenderer,
        iconRenderer: IconRenderer,
        currencyRenderer: CurrencyRenderer,
      },
      columnDefs: this.serverColumnDefs, // getting defs for server side fetch
      rowData: this.rowData,
      serverSideDatasource: this.serverSideDatasource,
      sideBar: this.filterPanelOpen
        ? {
            toolPanels: [
              {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
              },
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                },
              },
            ],
            defaultToolPanel: '',
          }
        : null,
      onGridReady: (event: GridReadyEvent<any>) => {
        this.agGrid = {
          api: event.api,
          // As of ag-grid@v31 ColumnApi has been deprecated and all methods will move to api.
          columnApi: event.columnApi,
        };

        [500, 1000, 2000, 3000, 4000, 5000, 6000, 10000, 20000].forEach(delay => {
          setTimeout(() => {
            // Views have their own column width set
            if (this.loadedView?.length == 0 && this.loadedViewData?.length == 0) {
              this.fitGrid === 'fit' ? this.agGrid.api.sizeColumnsToFit() : this.agGrid.columnApi.autoSizeAllColumns();
            }
          }, delay);
        });
        this.getGridState();
        // this.gridApi = params.api;
        this.adjustGridScroll();
        //   this.agGrid.api.sizeColumnsToFit();
        //   requestAnimationFrame(() => {
        //     this.agGrid.api.sizeColumnsToFit();
        // });
      },
      onColumnMoved: () => {
        this.currentColumnOrdering = this.agGrid.columnApi.getColumnState();
        this.getGridState();
      },
      onColumnVisible: () => {
        this.currentHiddenColumns = this.agGrid.columnApi.getColumnState();
        this.getGridState();
      },
      onColumnResized: () => {
        this.getGridState();
      },
      onColumnPinned: () => {
        this.getGridState();
      },
      onFilterChanged: () => {
        const filterModel = this.agGrid.api.getFilterModel();
        this.transformAndApplyFilters(filterModel);

        this.getGridState();
      },

      onSortChanged: () => {
        this.getGridState();
      },
      onFirstDataRendered: () => {
        this.getGridState();
        // Here you can also check if pivot mode has changed
        const isPivotMode = this.agGrid.columnApi.isPivotMode();
        if (isPivotMode !== this.prevPivotMode) {
          this.getPivotModeState(); // Replace with your actual method
          this.prevPivotMode = isPivotMode;
        }
      },
      suppressMenuHide: this.suppressMenuHide,
      tooltipShowDelay: 1000,
    } as unknown as GridOptions;
    const myGrid = this.el.shadowRoot.querySelector('#myGrid');
    if (!myGrid) throw new Error('#myGrid did not exist when trying to initalize agGrid');
    new window['agGrid'].Grid(myGrid, this.gridOptions);
  }

  // After apiCatalogId is pushed into localStorage
  handleGetPromotedLookupMethod = async () => {
    try {
      // Fetch the API Catalog ID, but don't await it since may not be needed yet
      this.getApiCatalogId().then(apiCatalogId => {
        if (!apiCatalogId) {
          this.gridError = true;
          throw new Error('apiCatalogId is undefined');
        }
      });

      localStorage.setItem('lookupApiMethodId', await this.getLookupApiMethod());

      let productList = state.catalogData;
      this.catalogList = productList;
    } catch (error) {
      this.gridError = true;
      console.error('Error performing API call:', error);
    }
  };

  handleGetInstanceAndMethod = async () => {
    localStorage.setItem('apiMethodId', await this.getApiMethodId());

    // Save data to local storage
    const catalogModificationP = this.getColumnDefinitions();

    const getConfiguredColumnDefs = UdpGridColumnDefs(await catalogModificationP);
    getConfiguredColumnDefs.forEach(column => {
      if (this.rowGrouping.includes(column.field)) {
        column.rowGroup = true;
        column.hide = true;
      }
    });
    this.configuredColumnDefs = getConfiguredColumnDefs;
    this.lookupsMap = this.getLookupsMap(await catalogModificationP);
  };

  async getLookupId(lookupsMap: any) {
    if (!lookupsMap) return null;

    const lookupToFilterOn = Object.values(lookupsMap).find((item: any) => item.filterOn);
    const lookupId = (lookupToFilterOn as any)?.value || '';

    const uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

    if (!lookupId || typeof lookupId !== 'string' || !uuidPattern.test(lookupId)) {
      console.error('Invalid lookupId', lookupId);
      return null;
    }

    // Call the function and await its result
    const data = await executeQueryWithParameters(
      {
        data: {
          id: lookupId,
        },
      },
      await this.getLookupApiMethod(),
      this.accessToken,
      this.tenantId,
    );
    this.filterChips = data.data;
    return lookupId;
  }

  getLookupsMap(queryColumnsData) {
    if (!queryColumnsData.length) return null;
    const map = {};
    const lookupColumns = queryColumnsData.filter(column => {
      const lookupValue = column.extendedProperties?.lookup;
      return !!lookupValue;
    });

    lookupColumns.forEach(lc => {
      map[lc.name] = {
        lookupKeyName: lc.extendedProperties?.lookupKeyName || '',
        lookupValueName: lc.extendedProperties?.lookupValueName || '',
        value: lc.extendedProperties?.lookup || '',
        filterOn: !!lc.filterOn || false,
      };
    });
    this.getLookupId(map);
    const keys = Object.keys(map);
    const keyString = keys[0];
    this.filterOn = keyString;
    return map;
  }

  async handleAdditionalSetup() {
    // Ensure handleGetPromotedLookupMethod is awaited to complete before proceeding.
    await this.handleGetPromotedLookupMethod();

    // After awaiting, check again for lookupApiMethodId in localStorage.
    if (await this.getLookupApiMethod()) {
      this.handleGetInstanceAndMethod();
    } else {
      // If lookupApiMethodId is still not set, it means there was an issue in handleGetPromotedLookupMethod.
      console.error('Failed to retrieve lookupApiMethodId - handleAdditionalSetup aborted.');
      // Return or handle the error as needed.
    }
  }

  handleSecondarySetup() {
    this.fetchAvailableGridActions();
    this.filterByCatalogObjectName(this.entityName);
    this.fetchDataForViews();
    this.transformBulkActions();
    this.handleGetAllHotlists();
  }

  getGridState() {
    if (this.gridOptions && this.agGrid) {
      const columnState = this.agGrid.columnApi.getColumnState();
      const columnGroupState = this.agGrid.columnApi.getColumnGroupState();
      const filterModel = this.filterObjectsWithGroups;
      const isPivotMode = this.agGrid.columnApi.isPivotMode();
      const sortModel = columnState.filter(column => column.sort != null);

      const gridState = [];
      const columnGridConfiguration = {
        gridStateTypeId: GridStateTypeEnums.Column,
        gridId: this.gridId,
        values: JSON.stringify(columnState),
      };
      const columnGroupGridConfiguration = {
        gridStateTypeId: GridStateTypeEnums.Column_Group,
        gridId: this.gridId,
        values: JSON.stringify(columnGroupState),
      };
      const columnSortGridConfiguration = {
        gridStateTypeId: GridStateTypeEnums.Column_Sort,
        gridId: this.gridId,
        values: JSON.stringify(sortModel),
      };
      const columnFilterGridConfiguration = {
        gridStateTypeId: GridStateTypeEnums.Column_Filter,
        gridId: this.gridId,
        values: JSON.stringify(filterModel),
      };
      const pivotGridConfiguration = {
        gridStateTypeId: GridStateTypeEnums.Pivot_Mode,
        gridId: this.gridId,
        values: JSON.stringify(isPivotMode),
      };
      gridState.push(columnGridConfiguration);
      gridState.push(columnGroupGridConfiguration);
      gridState.push(columnSortGridConfiguration);
      gridState.push(columnFilterGridConfiguration);
      gridState.push(pivotGridConfiguration);

      this.gridState = gridState;
      // Do something with columnState
    }
  }
  setColumnState(state: Array<{ [key: string]: any }>) {
    if (this.gridOptions && this.agGrid.columnApi) {
      const castedState = state as any;
      this.agGrid.columnApi.applyColumnState({ state: castedState, applyOrder: true });
    } else {
      console.warn('Grid options or Column API not available.');
    }
  }

  onFirstDataRendered(gridData) {
    if (this.hotListActive) {
      this.handleRemoveHotlist();
    }
    let gridConfigurations = [];
    this.loadedView = [this.fullViewData];
    // this.loadedViewTitle = this.fullViewData.name;
    this.loadedViewTitle = gridData.name;
    if (gridData && gridData.gridViewGridViewGridConfiguration) {
      gridData.gridViewGridViewGridConfiguration.forEach(config => {
        const state = config.gridViewGridConfigurationGridConfiguration;
        gridConfigurations.push({
          gridConfigurationId: state?.gridConfigurationId,
          gridStateTypeId: state?.gridStateTypeId,
        });

        switch (state.gridStateTypeId) {
          case 1:
            // this.agGrid.columnApi.applyColumnState({state: JSON.parse(state.values)});
            this.loadedViewData = state.values;
            this.setColumnState(JSON.parse(state.values));
            //this.agGrid.api.refreshServerSide();
            break;
          case GridStateTypeEnums.Pivot_Mode:
            this.agGrid.columnApi.setPivotMode(JSON.parse(state.values));

            break;
          case GridStateTypeEnums.Column_Sort:
            const currentColumnState = this.agGrid.columnApi.getColumnState();
            const newColumnState = JSON.parse(state.values);
            const mergedState = currentColumnState.map(col => newColumnState.find(newCol => newCol.colId === col.colId) || col);
            this.agGrid.columnApi.applyColumnState({ state: mergedState });

            break;
          case GridStateTypeEnums.Column_Filter:
            let filterObjects = JSON.parse(state.values);
            this.defaultSearchObject.forEach(defaultFilter => {
              let filterExists = false;

              for (let fo of filterObjects) {
                if (Array.isArray(fo.filters)) {
                  const match = fo.filters.find(f => f.searchField === defaultFilter.searchField);
                  if (match) {
                    match.isInitialFilterElement = true;
                    filterExists = true;
                    break;
                  }
                }
              }

              if (!filterExists) {
                const newFilter = this.buildSearchFilterElements(defaultFilter, true, true);
                const target = filterObjects.find(fo => Array.isArray(fo.filters));
                if (target) {
                  target.filters.push(newFilter);
                } else {
                  filterObjects.push({ filters: [newFilter] });
                }
              }
            });

            this.filterObject
              .flatMap(({ filterElements }) => filterElements)
              .forEach(filterElement => {
                const exists = filterObjects[0].filters.some(filter => filter.searchField === filterElement.searchField);
                if (!exists) {
                  filterObjects[0].filters.push(this.buildSearchFilterElements(filterElement, true, false));
                }
              });

            this.filterObjectsWithGroups = filterObjects;
            this.handleSideSheetButtonClick();
            break;
          case GridStateTypeEnums.Column_Group:
            this.agGrid.columnApi.setColumnGroupState(JSON.parse(state.values));
            break;
          default:
            break;
        }
      });
    }
    this.selectedGridViewGridConfigurations = gridConfigurations;
  }

  primaryActionClick(type: string) {
    if (type === 'advancedSearch') {
      this.advancedDrawerOpen = !this.advancedDrawerOpen;
    } else if (type === 'views') {
      this.viewDrawerOpen = !this.viewDrawerOpen;
    } else if (type === 'customSearch') {
      this.customSearchDrawerOpen = !this.customSearchDrawerOpen;
    } else {
      this.hotlistDrawerOpen = !this.hotlistDrawerOpen;
    }
  }

  // Opens the drop down menu of the gear icon in the header
  showGridOptions() {
    this.gridOptionsDrawerOpen = !this.gridOptionsDrawerOpen;
  }

  resetToOriginalFilters() {
    this.filterObjectsWithGroups = cloneDeep(this.initialFilterData.filterObjectsWithGroups);
    this.filterObject = cloneDeep(this.initialFilterData.filterObject);
    this.logicalSearchOperator = cloneDeep(this.initialFilterData.logicalSearchOperator);
    this.filterRenderList = cloneDeep(this.initialFilterData.filterRenderList);
    // this.updateChipLayout();
  }

  clearFilter() {
    this.refreshOnNoResults();

    if (this.gridOptions && this.agGrid) {
      const { api, columnApi } = this.agGrid;

      api.setQuickFilter('');
      columnApi.setRowGroupColumns([]);
      api.setFilterModel(null);
      api.setQuickFilter('');
      columnApi.applyColumnState({ state: this.defaultColumnState, applyOrder: true });

      // Refresh the grid after clearing all filters and settings
      // api.refreshClientSideRowModel();
      this.loadedView = [];
    }
    state.hotListActive = '';
  }
  refreshOnNoResults = () => {
    this.hotListActive = '';
    this.loadedView = [];
    // this.updateFilterObject(filterObject => {
    //   filterObject[0].filterElements = [];
    // });
    if (this.noResults) {
      lazySetupAgGridLibrary({ LicenseKey })
        .then(() => {
          this.handleSecondarySetup();
        })
        .catch(error => {
          this.gridError = true;
          console.error('Error refreshing the grid on no results:', error);
        });
    }
    this.updateServerSideDatasource('', 'advanced search sheet');
    this.resetToOriginalFilters();
    this.updateChipLayout();
    this.noResults = false;
    //this.setDefaultView();
  };

  // ONCLICK FUNCTIONS for grid-primary-bar and stencil-icon-button-grid-action-header
  exportToCsv() {
    if (this.agGrid) {
      const params = {};
      this.agGrid.api.exportDataAsCsv(params);
    }
  }
  fitColumns() {
    if (this.gridOptions && this.agGrid) {
      this.agGrid.api.sizeColumnsToFit();
    }
  }
  expandColumns() {
    if (this.gridOptions && this.agGrid && this.agGrid.columnApi) {
      this.agGrid.columnApi.autoSizeAllColumns();
    }
  }
  togglePanel(panelType) {
    const panelTypes = ['filters', 'columns'];
    panelTypes.forEach(type => {
      if (type !== panelType) {
        const otherPanelOpenKey = `${type}PanelOpen`;
        this[otherPanelOpenKey] = false; // Close other panel if open
      }
    });

    const panelOpenKey = `${panelType}PanelOpen`;
    this[panelOpenKey] = !this[panelOpenKey]; // Toggle the current panel

    // Create the toolPanel configuration based on the panelType
    const toolPanelConfig = {
      id: panelType,
      labelDefault: panelType.charAt(0).toUpperCase() + panelType.slice(1),
      labelKey: panelType,
      iconKey: panelType === 'filters' ? 'filter' : 'columns',
      toolPanel: panelType === 'filters' ? 'agFiltersToolPanel' : 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      },
    };

    // Update gridOptions to reflect new sidebar settings
    this.gridOptions.sideBar = this[panelOpenKey]
      ? {
          toolPanels: [toolPanelConfig],
          defaultToolPanel: panelType,
        }
      : null;

    this.agGrid.api.setSideBarVisible(this[panelOpenKey]);

    if (this[panelOpenKey]) {
      this.agGrid.api.openToolPanel(panelType);
    } else {
      this.agGrid.api.closeToolPanel();
    }

    // Apply Direct DOM Manipulation
    const sidebarElement = this.el.shadowRoot.querySelector('.ag-side-bar');
    if (sidebarElement instanceof HTMLElement) {
      sidebarElement.style.width = this[panelOpenKey] ? '200px' : 'initial';
    }
  }
  toggleServerSide = () => {
    this.isServerSide = !this.isServerSide;
  };
  getPivotModeState() {
    if (this.gridOptions && this.agGrid.columnApi) {
      return this.agGrid.columnApi.isPivotMode();
    }
    return false;
  }

  // VIEW FUNCTIONS
  showViews() {
    this.viewDrawerOpen = !this.viewDrawerOpen;
    this.sheetContent = 'views';
  }
  showViewSave() {
    this.saveDialogOpen = !this.saveDialogOpen;
  }
  resetDefaultView = () => {
    const domain = this.entityName;
    this.viewListRowData.forEach(item => {
      if (item.isDefault === true) {
        try {
          updateView(item.gridViewId, this.userId, this.gridId, this.apiCatalogId, this.accessToken, item.name, this.tenantId, 0, domain, item.gridViewVisibilityTypeId, data => {
            console.log('updateView, data: ', data);
          });
        } catch (error) {
          console.error('Error updating view, data:', error);
        }
      }
    });
  };
  setCurrentViewData(viewName: string, isDefault: boolean, gridViewVisibilityTypeId: number, gridViewId: string = '') {
    this.selectedGridId = gridViewId || this.selectedGridId;
    this.selectedViewName = viewName;
    this.selectedViewIsDefault = isDefault;
    this.selectedViewGridViewVisibilityTypeId = gridViewVisibilityTypeId;
  }
  openViewEdit(gridViewId: string, viewName: string, isDefault: boolean, gridViewVisibilityTypeId: number) {
    this.editDialogOpen = !this.editDialogOpen;
    this.setCurrentViewData(viewName, isDefault, gridViewVisibilityTypeId, gridViewId);
  }
  closeViewEdit() {
    this.editDialogOpen = !this.editDialogOpen;
  }
  openViewEditConfirmation(name: string, isPrivate: number, isDefault: boolean) {
    this.showViewEditConfirmation(true);
    this.setCurrentViewData(name, isDefault, isPrivate);
  }
  showViewEditConfirmation(show: boolean) {
    this.editViewConfirmationDialogOpen = show;
  }

  async handleViewFormSubmit(name: string, isPrivate: number, isDefault: boolean, columnState) {
    const userId = this.userId;
    const gridId = this.gridId;
    const apiCatalogId = this.apiCatalogId;
    const accessToken = this.accessToken;
    const tenantId = this.tenantId;
    const gridConfigurations = columnState; // The columnState from the argument
    const isDefaultNum: number = isDefault ? 1 : 0;
    const domain = await this.getEntityName();
    // if newDefault == 1, then we need to make sure any existing view with isDefault=1 is set to =0
    if (isDefaultNum == 1) {
      this.resetDefaultView();
    }

    // Now call saveView
    try {
      await saveView(userId, gridId, apiCatalogId, accessToken, name, domain, tenantId, gridConfigurations, isPrivate, isDefaultNum, data => {
        this.saveDialogOpen = !this.saveDialogOpen; // closes form
        this.showNotification = true;
        this.notificationMessage = `${data.gridView.name} has been saved successfully.`;
        this.notificationStatus = 'success';
        this.fetchDataForViews();
        setTimeout(() => {
          this.showNotification = !this.showNotification;
        }, 4000);
      });
    } catch (error) {
      console.error('Error saving data:', error);
    }
  }
  // Ensures each GridConfiguration has the correct GridConfigurationID for updating in the database
  updateGridConfigurationsWithIDsForUpdatingView(gridConfigs: any[]) {
    gridConfigs.forEach(config => {
      if (this.selectedGridViewGridConfigurations) {
        const gridConfigInfo = this.selectedGridViewGridConfigurations.find(item => item.gridStateTypeId === config.gridStateTypeId);
        config.gridConfigurationId = gridConfigInfo?.gridConfigurationId;
      }
    });
    return gridConfigs;
  }
  // Handler for saving changes to an existing GridView and its GridConfigurations
  async handleViewAndConfigurationsUpdate(name: string, isPrivate: number, isDefault: boolean, columnState) {
    const gridConfigurations = this.updateGridConfigurationsWithIDsForUpdatingView(columnState);
    const isDefaultNum: number = isDefault ? 1 : 0;
    const domain = await this.getEntityName();
    if (isDefaultNum == 1) {
      this.resetDefaultView();
    }

    try {
      await updateViewAndConfigurations(
        this.selectedGridId,
        this.userId,
        this.gridId,
        this.apiCatalogId,
        this.accessToken,
        name,
        this.tenantId,
        gridConfigurations,
        isDefaultNum,
        domain,
        isPrivate,
        () => {
          this.saveDialogOpen = !this.saveDialogOpen; // closes form
          this.showViewEditConfirmation(false);
          this.showNotification = true;
          this.notificationMessage = `${name} has been saved successfully.`;
          this.notificationStatus = 'success';
          this.fetchDataForViews();
          setTimeout(() => {
            this.showNotification = !this.showNotification;
          }, 4000);
        },
      );
    } catch (error) {
      console.error('Error saving data:', error);
    }
  }

  // Used for both hotlists and views
  toggleDeleteDialog = type => {
    switch (type) {
      case 'hotlist':
        this.confirmDialog = !this.confirmDialog;
        break;
      case 'view':
        this.confirmDialogDeleteView = !this.confirmDialogDeleteView;
        break;
      default:
        break;
    }
  };
  updateDefaultView = item => {
    var newDefault = item.isDefault ? 0 : 1;
    const domain = this.entityName;
    // if newDefault == 1, then we need to make sure any existing view with isDefault=1 is set to =0
    if (newDefault == 1) {
      this.resetDefaultView();
    }
    try {
      updateView(
        item.gridViewId,
        this.userId,
        this.gridId,
        this.apiCatalogId,
        this.accessToken,
        item.name,
        this.tenantId,
        newDefault,
        domain,
        item.gridViewVisibilityTypeId,
        data => {
          console.log('updateView, data:', data);
        },
      ).then(() => {
        this.fetchDataForViews();
        this.confirmDialog = false;
        this.showNotification = true;
        this.notificationMessage = `Updated default view.`;
        this.notificationStatus = 'success';
        setTimeout(() => {
          this.showNotification = !this.showNotification;
        }, 4000);
      });
    } catch (error) {
      this.gridError = true;
      console.error('Error updating view, data:', error);
    }
  };
  async handleViewUpdate(gridId: any, name: string, isPrivate: number, isDefault: boolean) {
    var isDefaultNum = isDefault ? 1 : 0;
    const domain = await this.getEntityName();

    // if this isDefault is true, then we need to make sure any existing view with isDefault=1 is set to =0
    if (isDefaultNum == 1) {
      this.resetDefaultView();
    }
    try {
      updateView(gridId, this.userId, this.gridId, this.apiCatalogId, this.accessToken, name, this.tenantId, isDefaultNum, domain, isPrivate, data => {
        console.log('updateView, data:', data);
      }).then(() => {
        this.fetchDataForViews();
        this.confirmDialog = false;
        this.showNotification = true;
        this.notificationMessage = `Updated view.`;
        this.notificationStatus = 'success';
        setTimeout(() => {
          this.showNotification = !this.showNotification;
        }, 4000);
      });
    } catch (error) {
      this.gridError = true;
      console.error('Error updating view, data:', error);
    }
  }
  fetchDataForViews = async () => {
    try {
      let apiCatalogId = await this.getApiCatalogId();
      if (!apiCatalogId) {
        console.warn('apiCatalogId is not yet defined');
        return;
      }

      const token = this.accessToken;
      const userId = this.userId;
      const gridId = this.gridId;

      getAvailableViews(userId, gridId, apiCatalogId, await this.getEntityName(), token, data => {
        this.viewListRowData = data; // Ensure this correctly updates the state
        this.setDefaultView();
      });
      this.updateForConfiguredColumnDefs = false;
    } catch (error) {
      this.gridError = true;
      console.error('Error fetching data for views:', error);
    }
  };
  setDefaultView = () => {
    const defaultViewObj = this.viewListRowData.find(item => item.isDefault === true);
    if (defaultViewObj) {
      const callback = data => {
        this.onFirstDataRendered(data);
      };
      const token = this.accessToken;
      getSelectedView(defaultViewObj.gridViewId, token, callback);
    }
  };
  openDeleteViewConfirmation = gridViewId => {
    this.viewId = gridViewId;
    this.confirmDialogDeleteView = true;
  };
  handleDeleteView = () => {
    // alert(`Delete view with ID:) ${viewId}`)
    this.confirmDialogDeleteView = false;
    const callback = () => {
      // Will send to grid to update view
      getAvailableViews(this.userId, this.gridId, this.apiCatalogId, this.entityName, this.accessToken, data => {
        this.viewListRowData = data; // Ensure this correctly updates the state
      });
    };
    deleteSelectedView(this.viewId, this.accessToken, callback);
    this.showNotification = true;
    this.notificationMessage = `Deleted view`;
    this.notificationStatus = 'success';
    setTimeout(() => {
      this.showNotification = !this.showNotification;
    }, 4000);
  };

  // HOTLIST FUNCTIONS
  showHotlist() {
    this.hotlistDrawerOpen = !this.hotlistDrawerOpen;
    this.businessObjectKey = null;
    this.logicalSearchOperator = 2;
  }
  // set hotlist form open
  toggleHotlistForm = () => {
    this.showHotlistForm = !this.showHotlistForm;
  };
  handleGetAllHotlists = async () => {
    try {
      const hotlistData = await getAllHotlists(this.tenantId, this.accessToken);
      this.allHotlists = hotlistData;
    } catch (error) {
      console.error('Error fetching hotlists:', error);
    }
  };
  // Add a hotlist item single
  handleSetSingleHotlistItem = id => {
    this.showNotification = true;
    this.notificationMessage = `Record added to hotlist`;
    this.notificationStatus = 'success';
    setTimeout(() => {
      this.showNotification = !this.showNotification;
    }, 4000);
    //const token = this.accessToken;
    this.hotlistDrawerOpen = !this.hotlistDrawerOpen;
    const businessObjectKey = this.dataRowClicked.businessObjectKey;
    // Update the dataObject
    const dataObject = {
      hotListId: id, // Assuming id is already defined
      contextDate: '2023/01/01',
      data: JSON.stringify(this.dataRowClicked),
      entityBusinessObjectKey: businessObjectKey,
      currentUserId: '8fe3b5ce-6a94-4ff4-b35c-be2596ea1380',
    };
    addRecordToAHotlist(dataObject, this.tenantId, this.accessToken);
  };
  // delete a selected hotlist
  confirmRequestDelete = hotListId => {
    this.confirmDialog = !this.confirmDialog;
    this.hotListId = hotListId;
  };
  handleRequestDeleteHotlist = () => {
    deleteAHotlist(this.hotListId, this.tenantId, this.accessToken)
      .then(() => {
        this.handleGetAllHotlists();
        this.confirmDialog = false;
      })
      .catch(error => {
        console.error('Error deleting hotlist:', error);
        // handle any errors here
      });
  };
  // convert hotlist to payload
  mapHotlistsToPayload(hotlistIdentifiers) {
    return hotlistIdentifiers.map(keyID => ({
      searchField: this.hotListIdentifier,
      searchOperator: '=',
      searchValue: keyID,
    }));
  }
  updateFilterObjectWithPayload(hotlistSearchKeys) {
    // clear view if active
    this.loadedView = [];
    this.resetToOriginalFilters();
    this.updateChipLayout();

    const payload = this.mapHotlistsToPayload(hotlistSearchKeys);
    this.hotListCount = payload.length;
    this.updateFilterObject(() => [
      {
        pageNumber: 1,
        pageSize: 20,
        filterElements: payload,
        orderElements: [],
        groupingType: '',
        groupProperty: [],
        groupOperationList: [],
        eagerLoad: false,
        logicalSearchOperator: 2,
        type: 'hotlist',
      },
    ]);
    this.updateServerSideDatasource('', 'updateFilterObjectWithPayload');
    this.hotlistDrawerOpen = false;
  }
  // extract id to process for search object
  extractContactIDs = payload => {
    const contactIDs = payload.records.map(record => {
      const dataObject = JSON.parse(record.data);
      return dataObject[this.hotListIdentifier];
    });
    return contactIDs;
  };
  // view a selected hotlist
  handleRequestHotlist = async hotlistID => {
    try {
      // Await for the Promise to resolve and then store the result
      const hotloadList = await loadAHotlist(hotlistID, this.tenantId, this.accessToken);

      // Process the data with extractContactIDs
      const contactIDs = this.extractContactIDs(hotloadList);
      this.hotListActive = hotloadList.name;
      state.hotListActive = this.hotListActive;

      // Set full list hotlist data
      this.hotlistSelectedFull = hotloadList;

      // Send the processed data to sendAsSearchObject
      this.updateFilterObjectWithPayload(contactIDs);
    } catch (error) {
      // Handle any errors that occur during the API call
      this.gridError = true;
      console.error('Error in handleRequestHotlist:', error);
    }
  };
  handleButtonClick = cellValue => {
    // Define the callback
    const callback = data => {
      // Will send to grid to update view
      this.onFirstDataRendered(data);
      this.loadedGridView = data;
    };

    const token = this.accessToken;
    getSelectedView(cellValue, token, callback);

    this.viewDrawerOpen = !this.viewDrawerOpen;
  };
  // Submit to save hotlist
  handleSaveHotlist = async (name: string, isPrivate: boolean) => {
    const hostListRequestBody = {
      domain: this.entityName,
      name: name,
      tenantId: this.tenantId,
      isPrivate: isPrivate,
      createdBy: this.userId,
      hotListDataSourceId: 1,
      hotListTypeId: 2,
    };

    try {
      await createAHotlist(hostListRequestBody, this.tenantId, this.accessToken);
      this.showHotlistForm = false;
      this.handleGetAllHotlists();
    } catch (error) {
      console.error('Error creating hotlist:', error);
      // handle any errors here
    }
  };

  // FILTER CHIP FUNCTIONS
  hasInitializedDefaultFilter = false; // Add this flag as a property in your class
  defaultFilterElements: any; // Replace any with your specific type
  activeChips: any[] = [];

  handleViewChipDelete = () => {
    // Your delete logic here
    // alert('Chip deleted!');
    this.loadedView = [];
    this.clearFilter();
  };

  handleRemoveHotlist = () => {
    this.hotListActive = '';
    this.loadedView = [];
    this.clearFilter();
    state.hotListActive = '';
  };

  fullClearSearch = () => {
    // this.filterObjectsWithGroups[0].filters.length = 0; // clears the form UI

    // this.updateFilterObject(() => [
    //   {
    //     pageNumber: 1,
    //     pageSize: 20,
    //     filterElements: [],
    //     orderElements: [],
    //     groupingType: '',
    //     groupProperty: [],
    //     groupOperationList: [],
    //     eagerLoad: false,
    //     logicalSearchOperator: 2,
    //     type: 'advanced',
    //   },
    // ]);
    this.resetToOriginalFilters();

    this.hotListActive = null;
    state.hotListActive = null;
    this.updateServerSideDatasource('', 'clearSearch');
  };

  setFilterFromChips(chipText: string) {
    const isChipValid = this.filterChips.some(chip => chip.description === chipText);
    if (!isChipValid) {
      return;
    }

    const index = this.activeChips.findIndex(chip => chip.description === chipText);
    // Initialize defaultFilterElements once, and only once
    if (!this.hasInitializedDefaultFilter) {
      if (this.filterObject && this.filterObject[0] && this.filterObject[0].filterElements) {
        this.defaultFilterElements = JSON.parse(JSON.stringify(this.filterObject[0].filterElements));
        this.hasInitializedDefaultFilter = true;
      }
    }

    // Toggle the chip on or off in activeChips
    if (index !== -1) {
      this.activeChips.splice(index, 1);
    } else {
      const chip = this.filterChips.find(chip => chip.description === chipText);
      this.activeChips.push(chip);
    }

    this.updateFilterObject(filterObject => {
      // Clear existing FirstName filters from filterElements
      filterObject[0].filterElements = filterObject[0].filterElements.filter(element => {
        return element.searchField !== this.filterOn;
      });

      // Re-add filters based on activeChips
      for (const chip of this.activeChips) {
        const searchValue = chip[this.filterOn] ? chip[this.filterOn] : chip.description;
        filterObject[0].filterElements.push({
          searchField: this.filterOn, // @todo: make this dynamic from api
          searchOperator: '=',
          searchValue: searchValue,
        });
      }

      // If all chips are deactivated, revert to default pre-set filter
      if (this.activeChips.length === 0) {
        filterObject[0].filterElements = JSON.parse(JSON.stringify(this.defaultFilterElements));
      }
    });

    this.logicalSearchOperator = 2;
    // Update the server-side datasource to apply the new filters
    this.updateServerSideDatasource('', 'setFilterChips');
  }

  setFilterSelections(filter) {
    const currentFilterObject = this.filterRenderList[0];
    currentFilterObject.filterElements = [...currentFilterObject.filterElements, filter];
    // Update the filterRenderList with the modified filter object
    this.filterRenderList = [currentFilterObject, ...this.filterRenderList.slice(1)];
  }

  // ADVANCED SEARCH FUNCTIONS
  showCustomSearch() {
    this.customSearchDrawerOpen = !this.customSearchDrawerOpen;
  }
  showAdvancedSearch() {
    this.advancedDrawerOpen = !this.advancedDrawerOpen;
    this.logicalSearchOperator = 1;
    this.setSearchStart = false;
  }

  setGroupedFilterSelections(filter: any) {
    // Generate a unique ID for the new filter, you could use a different method
    const newFilterId = Date.now().toString(); // Simple example, consider using a more robust method

    // Try to find corresponding domain field from configuredColumnDefs because only grid columns configured in query builder could have an alias.
    const searchFieldAlias = this.configuredColumnDefs?.find(col => col.field.toLowerCase() === filter.searchField.toLowerCase())?.headerName;

    // Prepare the filter object with the new ID
    const newFilter = { ...filter, id: newFilterId, searchFieldAlias: searchFieldAlias };

    // Function to recursively find and update the correct node
    const addFilterToNode = (nodes: any[], nodeId: string, filter: any, otherId: any) => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          // Modify here: Include otherId as part of the newFilter object
          const newFilter = { ...filter, otherId }; // Merge otherId into the filter object
          node.filters.push(newFilter); // Add the updated filter object with otherId
          return true; // Stop searching when the correct node is updated
        }
        if (node.children && node.children.length > 0) {
          if (addFilterToNode(node.children, nodeId, filter, otherId)) {
            return true; // Stop searching if the correct node was found in the children
          }
        }
      }
      return false; // Node not found in this branch
    };

    // Attempt to add the new filter to the correct node
    addFilterToNode(this.filterObjectsWithGroups, this.advancedGroupSearchNodeId, newFilter, this.otherId);

    // Since we are modifying the array in place, we might need to trigger a state update manually
    this.filterObjectsWithGroups = [...this.filterObjectsWithGroups];
  }

  setGroupedSearchSections(groupId: string, logicalOperator: 'AND' | 'OR') {
    // Generate a unique ID for the new child section
    const newChildId = Date.now().toString(); // Example ID generation, consider using a UUID for production

    const newChild = {
      id: newChildId,
      filterId: groupId, //abc
      logicalOperator: logicalOperator,
      filters: [],
      children: [],
    };

    // Recursive function to find the correct node and add the new child
    const addChildToNode = (nodes: any[], nodeId: string, child: any) => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          if (!node.children) {
            node.children = []; // Ensure the children array exists
          }
          node.children.push(child);
          return true; // Node updated, stop the search
        }
        if (node.children && node.children.length > 0) {
          if (addChildToNode(node.children, nodeId, child)) {
            return true; // Child added in a deeper level
          }
        }
      }
      return false; // Node not found in this branch
    };

    // Attempt to add the new child to the node identified by groupId
    addChildToNode(this.filterObjectsWithGroups, groupId, newChild);

    // Trigger a state update to reflect the change
    this.filterObjectsWithGroups = [...this.filterObjectsWithGroups];
  }

  updateGroupLogicalOperator(groupId: string, logicalOperator: 'AND' | 'OR') {
    const updateOperator = (nodes: any[], nodeId: string, newOperator: 'AND' | 'OR') => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          if (node.id === 'root') {
            this.logicalSearchOperator = newOperator === 'AND' ? 1 : 2;
          }
          node.logicalOperator = newOperator; // Update the logical operator here
          return true; // Successfully updated, stop the search
        }
        if (node.children && updateOperator(node.children, nodeId, newOperator)) {
          return true; // Successfully updated in a deeper level
        }
      }
      return false; // Node not found
    };

    // Attempt to update the logical operator in the node identified by groupId
    if (updateOperator(this.filterObjectsWithGroups, groupId, logicalOperator)) {
      // If the group was found and updated, trigger a state update
      this.filterObjectsWithGroups = [...this.filterObjectsWithGroups];
    }
  }

  removeFilterSelection(filter) {
    this.setFilterFromChips(filter.searchValue);
    this.externalToggle = filter.searchValue;
    // Update filterRenderList with the current state of filterObject
    this.filterRenderList = this.filterObject.map(obj => ({
      ...obj,
      filterElements: obj.filterElements.map(fe => ({ ...fe })),
    }));

    // Remove the filter from filterObject
    this.updateFilterObject(filterObject => {
      filterObject.forEach(obj => {
        obj.filterElements = obj.filterElements.filter(item => {
          return !(item.searchField === filter.searchField && item.searchValue === filter.searchValue);
        });
      });
    });

    // Now remove the filter from filterRenderList
    this.filterRenderList.forEach(listItem => {
      listItem.filterElements = listItem.filterElements.filter(item => {
        return !(item.searchField === filter.searchField && (item.searchValue === filter.searchValue || filter.searchValue === ''));
      });
    });

    // Creating new copies for reactivity if needed
    this.filterRenderList = [...this.filterRenderList];

    // Update the server-side datasource if necessary
    this.updateServerSideDatasource('remove', 'remove items');
    this.agGrid.api.setFilterModel(this.filterObject[0].filterElements);

    this.removeFromSearchGroupUI(filter, this.filterObjectsWithGroups);

    this.getGridState();
  }

  removeFromSearchGroupUI(filterToRemove, filterObjectsWithGroups) {
    // Helper function to remove the filter by ID
    const removeFilterById = (filtersArray, filterId) => {
      return filtersArray.filter(filter => filter.id !== filterId);
    };

    // Recursive function to search and remove the filter
    const searchAndRemoveFilter = (groups, filterId) => {
      groups.forEach(group => {
        // Remove the filter from the current group if it matches the filter ID
        group.filters = removeFilterById(group.filters, filterId);

        // If there are children, recurse into them
        if (group.children && group.children.length > 0) {
          searchAndRemoveFilter(group.children, filterId);
        }
      });
    };

    // Start the recursive search and remove process
    searchAndRemoveFilter(filterObjectsWithGroups, filterToRemove.id);

    // Line to deal with full removal as the function to pin point additional nested elements is not yet available
    //this.filterObject[0].filterElements = []
  }

  handleGroupItemDelete(groupId) {
    // Recursive function to search for and remove the group by ID
    const searchAndRemoveGroup = (groups, groupId) => {
      return groups.reduce((acc, group) => {
        // If the current group matches the groupId, do not add it to the accumulator
        if (group.id === groupId) {
          return acc;
        }

        // Otherwise, add the group to the accumulator
        const newGroup = { ...group };

        // If the group has children, recurse to potentially remove from children
        if (newGroup.children && newGroup.children.length > 0) {
          newGroup.children = searchAndRemoveGroup(newGroup.children, groupId);
        }

        acc.push(newGroup);
        return acc;
      }, []);
    };

    // Start the recursive search and remove process
    this.filterObjectsWithGroups = searchAndRemoveGroup(this.filterObjectsWithGroups, groupId);
  }

  // Function to update filter list with groups
  updateFilterObjectsWithGroups = (searchField, searchOperator, searchValue, searchFieldAlias, originalSearchOperator, originalSearchValue) => {
    let found = false;
    for (let group of this.filterObjectsWithGroups) {
      for (let filter of group.filters) {
        if (filter.searchField === searchField && filter.searchValue === searchValue) {
          filter.searchOperator = searchOperator;
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (!found) {
      this.filterObjectsWithGroups[0].filters.push({
        searchField,
        searchOperator,
        searchValue,
        parentName: '',
        parentPath: 'filter.extendedProperties.parentPath',
        id: Date.now().toString(),
        otherId: `id-${Math.random().toString(36).substr(2, 9)}`,
        searchFieldAlias,
        originalSearchOperator,
        originalSearchValue,
      });
    }
  };

  @Listen('searchItemChanged')
  searchItemChangedHandler(event: CustomEvent) {
    let { searchField, searchOperator, searchValue, groupId, searchFieldAlias, type, originalSearchOperator, originalSearchValue } = event.detail;
    this.refreshChips = true;
    this.setSearchStart = true;
    // Function to check if the entry is valid and complete
    const isEntryValid = (field, operator, value) => {
      // Implement your specific validation logic here
      // For example, check if all fields are non-empty and meet certain criteria
      return field && operator && value && value.trim() !== '';
    };

    // Only apply the following logic if event is not from the native ag grid column filter
    if (!type || type === 'agGridColumnFilter') {
      let advancedSearchFilter = this.extendedSearchFilterList?.find(filter => filter.name.toLowerCase() === searchField.toLowerCase());
      if (advancedSearchFilter?.type?.toLowerCase() === 'datetime') {
        const configuredColumnDef = this.configuredColumnDefs?.find(col => col.field.toLowerCase() === searchField.toLowerCase());
        searchFieldAlias = configuredColumnDef?.headerName;
        if (searchOperator === this.filterOptions['equals'] && configuredColumnDef?.treatAsDate) {
          let dateFrom = new Date(searchValue);
          let dateTo = new Date(dateFrom);

          // Set dateTo to one day ahead
          dateTo.setDate(dateFrom.getDate() + 1);

          // Convert the dates to the required format (YYYY-MM-DD HH:mm:ss)
          const formatDate = (date, dateOnly: boolean = false) => {
            const year = date.getFullYear();
            const month = ('0' + (date.getMonth() + 1)).slice(-2);
            const day = ('0' + date.getDate()).slice(-2);
            return dateOnly ? `${year}-${month}-${day}` : `${year}-${month}-${day} 00:00:00`;
          };
          originalSearchOperator = searchOperator;
          originalSearchValue = searchValue;
          searchOperator = this.filterOptions['inRange'];
          searchValue = formatDate(dateFrom) + ',' + formatDate(dateTo);
        }
      }
    }

    // Update filter list with groups only if there's no groupId in the event details
    if (!groupId) {
      this.updateFilterObjectsWithGroups(searchField, searchOperator, searchValue, searchFieldAlias, originalSearchOperator, originalSearchValue);
    }

    // const filterObjectIndex = 0;
    const filterRenderListIndex = 0;

    const updateFilterList = (filterList, index) => {
      const operatorValue = searchOperator.value ? searchOperator.value : searchOperator;
      const existingFilterIndex = filterList[index].filterElements.findIndex(obj => obj.fieldId === event.detail.itemId);

      if (isEntryValid(searchField, searchOperator, searchValue)) {
        if (existingFilterIndex !== -1) {
          filterList[index].filterElements[existingFilterIndex] = {
            searchField,
            searchOperator: operatorValue,
            searchValue,
            groupId: 1,
            fieldId: event.detail.itemId,
            originalSearchOperator: originalSearchOperator,
            originalSearchValue: originalSearchValue,
          };
        } else {
          filterList[index].filterElements.push({
            searchField,
            searchOperator: operatorValue,
            searchValue,
            groupId: 1,
            fieldId: event.detail.itemId,
            originalSearchOperator: originalSearchOperator,
            originalSearchValue: originalSearchValue,
          });
        }
      } else {
        if (existingFilterIndex !== -1) {
          filterList[index].filterElements.splice(existingFilterIndex, 1);
        }
      }
    };

    // this.updateFilterObject(filterObject => {
    //   updateFilterList(filterObject, filterObjectIndex);
    // });
    const updatedFilterList = cloneDeep(this.filterRenderList);
    updateFilterList(updatedFilterList, filterRenderListIndex);

    // Immutably update the state for both lists
    this.filterObject = [...updatedFilterList];
    this.filterRenderList = [...updatedFilterList];
    this.filterObjectsWithGroups = [...this.filterObjectsWithGroups];
  }
  // searchItemChangeHandler ends...

  updateSearchObject(detail: { searchField: string; searchOperator: string; searchValue: any }) {
    this.updateFilterObject(filterObject => {
      // Append the new filter element to filterElements array
      filterObject[0].filterElements.push(detail);
    });
  }

  // Commented out filter options are currently not suppored in the regex
  filterOptions = {
    equals: '=',
    contains: 'LIKE',
    // notContains: 'NOT LIKE',
    notEqual: '<>',
    greaterThan: '>',
    lessThan: '<',
    greaterThanOrEqual: '>=',
    lessThanOrEqual: '<=',
    startsWith: 'STARTSWITH',
    // endsWith: 'ENDSWITH',
    // blank: 'IS NULL',
    // notBlank: 'IS NOT NULL',
    inRange: 'BETWEEN',
  };

  // FOR HANDLING NATIVE AG-grid filter
  transformAndApplyFilters(filterModel: { [key: string]: FilterDetail }) {
    let groupedFilterElements;
    let filterObjectsWithGroupsFilters = [];
    const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
    const generateUniqueId = () => 'id-' + Math.random().toString(36).substr(2, 9);

    if (this.hotListActive) {
      // first group all the hotlist origin filters
      groupedFilterElements = this.filterObject[0].filterElements.map(element => ({
        ...element,
        searchField: capitalizeFirstLetter(element.searchField),
        groupId: 2,
      }));
      // slicing because need to remove duplicate native filters
      let slicedGroupedFilterElements = groupedFilterElements.slice(0, this.hotListCount);
      groupedFilterElements = slicedGroupedFilterElements;

      // this.filterRenderList = [...this.filterRenderList, ...groupedFilterElements]
      this.filterObject[0].filterElements.map(element => this.filterRenderList.push(element));
    }

    var groupCount = 1;
    for (const [column, filterDetails] of Object.entries(filterModel)) {
      if (filterDetails && filterDetails.type) {
        let selectedFilterOptions = this.filterOptions[filterDetails.type];

        let value = filterDetails.filter;
        const configuredColumnDef = this.configuredColumnDefs?.find(col => col.field === column);
        let searchFieldAlias = configuredColumnDef?.headerName;
        let originalSearchOperator = '';
        let originalSearchValue = '';

        // If you are filtering a date columm
        if (filterDetails.filterType === 'date') {
          const treatAsDate = configuredColumnDef?.treatAsDate;

          // If you are filtering between two dates
          if (filterDetails.type === 'inRange') {
            value = filterDetails.dateFrom + ',' + filterDetails.dateTo;
          } else if (filterDetails.type === 'equals' && treatAsDate) {
            let dateFrom = new Date(filterDetails.dateFrom);
            let dateTo = new Date(dateFrom);

            // Set dateTo to one day ahead
            dateTo.setDate(dateFrom.getDate() + 1);

            // Convert the dates to the required format (YYYY-MM-DD HH:mm:ss)
            const formatDate = (date, dateOnly: boolean = false) => {
              const year = date.getFullYear();
              const month = ('0' + (date.getMonth() + 1)).slice(-2);
              const day = ('0' + date.getDate()).slice(-2);
              return dateOnly ? `${year}-${month}-${day}` : `${year}-${month}-${day} 00:00:00`;
            };
            originalSearchOperator = selectedFilterOptions;
            originalSearchValue = formatDate(dateFrom, treatAsDate);
            selectedFilterOptions = this.filterOptions['inRange'];
            value = formatDate(dateFrom) + ',' + formatDate(dateTo);
          } else {
            value = filterDetails.dateFrom;
          }
        } else if (filterDetails.filterType === 'text') {
          originalSearchOperator = selectedFilterOptions;
          originalSearchValue = filterDetails.filter;
          value = filterDetails.filter;
        } else if (filterDetails.filterType === 'number') {
          originalSearchOperator = selectedFilterOptions;
          originalSearchValue = filterDetails.filter;
          if (filterDetails.type === 'inRange') {
            value = filterDetails.filter + ',' + filterDetails.filterTo;
          } else {
            value = filterDetails.filter;
          }
        }

        const setDetail = {
          searchField: column,
          searchOperator: selectedFilterOptions,
          searchValue: value,
          searchFieldAlias: searchFieldAlias,
          originalSearchOperator: originalSearchOperator || '',
          originalSearchValue: originalSearchValue || '',
        };

        if (this.hotListActive) {
          groupedFilterElements.push({
            groupId: groupCount,
            searchField: capitalizeFirstLetter(setDetail.searchField),
            searchOperator: setDetail.searchOperator,
            searchValue: String(setDetail.searchValue),
            searchFieldAlias: setDetail.searchFieldAlias,
            originalSearchOperator: setDetail.originalSearchOperator,
            originalSearchValue: setDetail.originalSearchValue,
          });
          filterObjectsWithGroupsFilters.push({
            searchField: setDetail.searchField,
            searchOperator: setDetail.searchOperator,
            searchValue: setDetail.searchValue,
            id: generateUniqueId(),
            otherId: generateUniqueId(),
            parentName: '',
            parentPath: 'filter.extendedProperties.parentPath',
            searchFieldAlias: setDetail.searchFieldAlias,
            originalSearchOperator: setDetail.originalSearchOperator,
            originalSearchValue: setDetail.originalSearchValue,
          });
          if (groupCount === 1) {
            groupCount = 3;
          } else {
            groupCount++;
          }
          continue;
        }

        //this.updateSearchObject(detail);
        this.searchItemChangedHandler({
          detail: setDetail,
          initCustomEvent: function (): void {
            throw new Error('Function not implemented.');
          },
          bubbles: false,
          cancelBubble: false,
          cancelable: false,
          composed: false,
          currentTarget: undefined,
          defaultPrevented: false,
          eventPhase: 0,
          isTrusted: false,
          returnValue: false,
          srcElement: undefined,
          target: undefined,
          timeStamp: 0,
          type: 'agGridColumnFilter',
          composedPath: function (): EventTarget[] {
            throw new Error('Function not implemented.');
          },
          initEvent: function (): void {
            throw new Error('Function not implemented.');
          },
          preventDefault: function (): void {
            throw new Error('Function not implemented.');
          },
          stopImmediatePropagation: function (): void {
            throw new Error('Function not implemented.');
          },
          stopPropagation: function (): void {
            throw new Error('Function not implemented.');
          },
          NONE: 0,
          CAPTURING_PHASE: 1,
          AT_TARGET: 2,
          BUBBLING_PHASE: 3,
        });
      }
    }

    if (this.hotListActive) {
      this.updateFilterObject(filterObject => {
        filterObject[0].filterElements = groupedFilterElements;
        filterObject[0].filterGroups = [
          {
            groupId: 1,
            logicalSearchOperator: 1,
            parentGroupId: null,
          },
          {
            groupId: 2,
            logicalSearchOperator: 2,
            parentGroupId: 1,
          },
        ];

        // Adds additional native grid filters
        for (let i = 3; i < groupCount; i++) {
          filterObject[0].filterGroups.push({
            groupId: i,
            logicalSearchOperator: 1,
            parentGroupId: 1,
          });
        }

        filterObject[0].logicalSearchOperator = 1;
        filterObject[0].type = 'advanced';
      });

      this.logicalSearchOperator = 1;
      this.updateChipLayout();

      this.filterObjectsWithGroups[0].filters = filterObjectsWithGroupsFilters;
      this.refreshChips = true;
    }
  }

  handleSideSheetButtonClick = async () => {
    this.groupFilter();
    this.customSearchDrawerOpen = false;
  };

  // advanced search grouping
  handleAddNewFilter = (groupId: string, otherId: string) => {
    // Process the groupId to add a new filter
    this.advancedGroupSearchNodeId = groupId;
    this.otherId = otherId;
  };

  handleAddNewGroup = (groupId: string, logicalOperator: 'AND' | 'OR') => {
    this.advancedGroupSearchNodeId = groupId;
    // Pass the logicalOperator to setGroupedSearchSections
    this.setGroupedSearchSections(groupId, logicalOperator);
  };

  updateLogicalOperator = (groupId: string, logicalOperator: 'AND' | 'OR') => {
    // Implement the logic to update the logical operator for the group
    this.updateGroupLogicalOperator(groupId, logicalOperator);
  };

  @Watch('externalPayload')
  externalPayloadChanged(newValue: any, oldValue: any) {
    if (newValue !== oldValue) {
      this.handleTransformSearchData();
    }
  }

  @Watch('dataEmitterRequest')
  dataEmitterChanged(newValue: any, oldValue: any) {
    if (newValue !== oldValue) {
      this.handleDataEmitterRequest(newValue);
    }
  }

  isEmptyObject = obj => {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
  };

  handleDataEmitterRequest = async newValue => {
    // Check if newValue is null, undefined, or an empty object
    if (newValue === null || newValue === undefined || this.isEmptyObject(newValue)) {
      // Handle the case where newValue is not usable
      return;
    }

    const emitterSearchObject = {
      pageNumber: 1,
      pageSize: 20,
      filterElements: [newValue],
      orderElements: [],
      groupingType: '',
      groupProperty: [],
      groupOperationList: [],
      eagerLoad: false,
      logicalSearchOperator: 1,
      type: 'advanced',
    };

    try {
      const response = await executeQueryAdHoc(emitterSearchObject, await this.getApiMethodId(), this.accessToken, this.tenantId, '');
      this.externalEmitter.emit(response); // Emitting the response data
    } catch (error) {
      this.gridError = true;
      console.error('Error fetching data:', error);
    }
  };

  handleTransformSearchData = () => {
    const modifiedSearchObjects = transformSearchData(this.externalPayload, this.searchKey);

    // Merge the filterElements
    if (modifiedSearchObjects.filterElements) {
      this.updateFilterObject(filterObject => {
        filterObject[0].filterElements.push(...modifiedSearchObjects.filterElements);
      });
    }

    // Handle filterGroups (assuming you want to replace the existing ones)
    if (modifiedSearchObjects.filterGroups) {
      this.updateFilterObject(filterObject => {
        filterObject[0].filterGroups = modifiedSearchObjects.filterGroups;
      });
    }

    // Extract the first element to send as an individual object
    const firstFilterElement = this.filterObject[0].filterElements[0];

    // Create a new CustomEvent with the first object as detail (instead of the entire array)
    const filterElementEvent = new CustomEvent('filterElementChange', {
      detail: firstFilterElement,
    });

    // Pass the event to the handler function
    this.searchItemChangedHandler(filterElementEvent);

    this.updateServerSideDatasource('', 'handleTransformSearchData');
  };

  // FOR HANDLING IF SELECT OR GROUPING IN ADVANCED SEARCH
  groupFilter = () => {
    const searchGroupFilterPayload = buildSearchPayload(this.filterObjectsWithGroups);
    this.updateFilterObject(() => [searchGroupFilterPayload]);
    this.setSearchStart = true;
  };

  updateFilterObjectsWithInput(filterObjectsWithGroups, filterObject) {
    // FIXME: This appears to be modifying the filterObject input
    const updateFilters = (filters, filterElements) => {
      filters.forEach(filter => {
        const inputElement = filterElements.find(element => element.fieldId === filter.otherId);
        if (inputElement) {
          filter.searchOperator = inputElement.searchOperator;
          filter.searchValue = inputElement.searchValue;
          filter.originalSearchOperator = inputElement.originalSearchOperator;
          filter.originalSearchValue = inputElement.originalSearchValue;
        }
      });
    };

    const traverseAndUpdate = objects => {
      objects.forEach(obj => {
        // Update the current level filters
        if (obj.filters) {
          updateFilters(obj.filters, filterObject[0].filterElements);
        }
        // Recursively update children, if any
        if (obj.children && obj.children.length > 0) {
          traverseAndUpdate(obj.children);
        }
      });
    };

    // Start the traversal and update process
    traverseAndUpdate(filterObjectsWithGroups);
    this.updateChipLayout();
  }

  handleSearchItemChange() {
    const updatedFilterObjectsWithGroups = cloneDeep(this.filterObjectsWithGroups);
    const updatedFilterObject = cloneDeep(this.filterObject);

    this.updateFilterObjectsWithInput(updatedFilterObjectsWithGroups, updatedFilterObject);
    this.filterObjectsWithGroups = updatedFilterObjectsWithGroups;
    this.filterObject = updatedFilterObject;
  }

  collectAllFilters(filterGroup, level = 0) {
    let allFilters = [];

    // Collect top-level filters and include the current level
    if (filterGroup.filters && filterGroup.filters.length > 0) {
      allFilters = allFilters.concat(filterGroup.filters.map(filter => ({ ...filter, level })));
    }

    // Recursively collect filters from children, incrementing the level
    if (filterGroup.children && filterGroup.children.length > 0) {
      filterGroup.children.forEach(child => {
        allFilters = allFilters.concat(this.collectAllFilters(child, level + 1));
      });
    }

    return allFilters;
  }

  updateChipLayout = () => {
    requestAnimationFrame(() => {
      const chipsContainer = this.el.shadowRoot ? this.el.shadowRoot.getElementById('advanced-group-search-chips') : document.getElementById('advanced-group-search-chips');

      if (chipsContainer) {
        // Force reflow if necessary
        chipsContainer.offsetWidth;

        const containerWidth = chipsContainer.getBoundingClientRect().width;
        let totalChipWidth = 0;
        const chipWidths = Array.from(chipsContainer.querySelectorAll('stencil-chip')).map(chip => chip.getBoundingClientRect().width);
        let maxVisible = 1;

        for (let width of chipWidths) {
          if (totalChipWidth + width + 400 > containerWidth) break;
          if (maxVisible >= this.maxChipsAllowed) break; // Limit the number of visible chips to 7, but only after width check
          totalChipWidth += width;
          maxVisible++;
        }
        this.allFilters = this.filterObjectsWithGroups.reduce((acc, curr) => {
          return acc.concat(this.collectAllFilters(curr));
        }, []);

        if (this.hideDefaultSearchFilterChips) {
          this.allFilters = this.allFilters.filter(f => !f.isInitialFilterElement);
        }

        this.visibleChips = [...this.allFilters.slice(0, maxVisible)];
        this.overflowChips = [...this.allFilters.slice(maxVisible)];

        this.refreshChips = false;
      } else {
        console.warn('Chips container not found');
      }
    });
  };

  // Fetch recentSearches from local storage
  getRecentSearches = () => {
    const recentSearchesString = localStorage.getItem('recentSearches');
    if (recentSearchesString == null) {
      return;
    }
    const recentSearches: any[] = JSON.parse(recentSearchesString);
    var currentGridSearchHistoryObj = recentSearches.find(item => item.gridUID === this.gridUID);
    if (!currentGridSearchHistoryObj) {
      this.recentSearches = [];
    } else {
      this.recentSearches = recentSearches.length > 0 ? currentGridSearchHistoryObj.searchHistory : [];
    }
    this.recentSearchesOptions = this.recentSearches.map(item => ({
      label: item.timestamp,
      value: item.searchObj,
    }));
  };
  // Handle selecting a recent search from the drop down udp-selector
  selectRecentSearch = e => {
    this.fullClearSearch(); // Clear previous searches first
    var selectedSearchObj = e.detail.value;
    this.updateFilterObject(filterObject => {
      filterObject[0] = selectedSearchObj;
    });
    this.filterRenderList[0] = selectedSearchObj;
    this.isRecentSearch = true;
    this.updateServerSideDatasource('', 'advanced grouped search');
    // this.customSearchDrawerOpen = false; // uncomment if want advanced search sidesheet to close after clicking

    var filterObjectsWithGroupsTemp = this.filterObjectsWithGroups;
    selectedSearchObj.filterElements.map((tempFilter, index) => {
      filterObjectsWithGroupsTemp[0].filters[index] = { ...tempFilter };
    });
    this.filterObjectsWithGroups = filterObjectsWithGroupsTemp;
  };

  @Watch('allFilters')
  allFiltersChanged() {
    if (this.refreshChips) {
      this.updateChipLayout();
    }
  }

  // BULK ACTIONS FUNCTIONS
  fetchAvailableGridActions = () => {
    if (this.restrictedGridFeatures?.BulkActions && !this.userPermissions.some(permission => permission.name === this.restrictedGridFeatures.BulkActions)) {
      localStorage.setItem('bulkActions', JSON.stringify([]));
      return;
    }
    const callback = data => {
      // Will send to grid to update view
      localStorage.setItem('bulkActions', JSON.stringify(data));
      this.bulkActions = data;
    };

    const token = this.accessToken;

    getAvailableGridActions(this.gridId, token, this.restrictBulkActionsPermissions, this.userPermissions, callback);

    this.updateServerSideDatasource('', 'configuredColumnDefs');
  };

  async handleSelectedBulkOption(event: CustomEvent) {
    // Assuming `event.detail` includes the selected option's identifier, like `value`
    const { value } = event.detail; // You might need to adjust based on the actual structure
    // Find the index of the selected option based on its value

    if (this.gridOptions && this.agGrid) {
      const selectedNodes = this.agGrid.api.getSelectedNodes();

      // Extract the data from selected nodes
      let selectedData = selectedNodes.map(node => node.data);

      // Extract just the ids from the selectedData
      const selectedIds = selectedData.map(data => data.id);

      // Emit the selectedIds array so parent components can listen to it
      this.selectedIdsChanged.emit(selectedIds);

      let actionData;

      if (this.bulkActionFunctions[value.value]) {
        //There exists a bulk action function corresponding to the actions value. Passing the function as the actionData to the action executor
        const selectedBulkActionFunction = this.bulkActionFunctions[value.value];
        actionData = { execute: () => selectedBulkActionFunction(selectedData) };
      } else {
        actionData = selectedData;
      }

      await this.executeBulkAction(value.actionId, actionData);
    }
  }

  @Watch('bulkActions')
  transformBulkActions() {
    if (this.bulkActions) {
      this.formattedBulkActions = this.bulkActions.map(action => ({
        label: action.name,
        value: action,
      }));
    }
  }

  getSelectedRows() {
    if (this.agGrid?.api) {
      const selectedNodes = this.agGrid.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      return selectedData;
    }
    return [];
  }

  render() {
    const filterPanelWidth = this.filterPanelOpen ? '200px' : 'initial';
    const shouldShowSecondRow =
      (this.setSearchStart && this.filterObjectsWithGroups?.[0]?.filters?.[0]?.searchValue) ||
      this.filterChips.length > 0 ||
      this.loadedView.length > 0 ||
      this.hotListActive ||
      this.kpiValues.length > 0 ||
      this.selectedRowCount > 0 ||
      this.visibleChips?.length > 0;

    const shouldHideSecondRow = !shouldShowSecondRow;
    if (shouldShowSecondRow) {
      this.gridHeight = 'calc(100vh - 245px)';
    } else {
      this.gridHeight = 'calc(100vh - 200px)';
    }

    const actionMenuItems = this.getOverflowActionList(this.overflowActionMenuItems, this.selectedRowParams);
    return (
      <div>
        {this.openActionPopoverMenu && (
          <udp-pop-over
            isOpen={this.openActionPopoverMenu}
            anchorElement={this.actionMenuAnchorElement}
            popoverWidth="170px"
            popoverMaxHeight="250px"
            handleOnClose={event => {
              this.handleOnCloseActionPopover(event);
            }}
          >
            <div class="overflowActionButtons">{actionMenuItems}</div>
          </udp-pop-over>
        )}
        <div>
          {this.showNotification && (
            <udp-notification
              active={this.showNotification}
              message={this.notificationMessage}
              status={this.notificationStatus}
              userCancel
              style={{ zIndex: '999991', transform: 'translateY(20px)' }}
            ></udp-notification>
          )}
        </div>
        <style>
          {`:host ::slotted(.ag-side-bar) {
            width: ${filterPanelWidth} !important;
          }`}
        </style>
        {/* <button onClick={() =>this.showCatalogDataTest()} >Look at ctlg</button> */}
        {/* <button onClick={()=> this.groupFilter()} >Test payload build grouped</button>
      <button onClick={()=> this.updateFiltersFromGroupSerch()} >Test data to group search array</button> */}

        <grid-primary-bar
          title={this.gridBarTitle}
          customSaveViewClick={() => this.showViewSave()}
          customShowViewClick={() => this.showViews()}
          customShowHotlistClick={() => this.showHotlist()}
          customShowCustomSearchClick={() => this.showCustomSearch()}
          customClearClick={() => this.clearFilter()}
          customExpandClick={() => this.expandColumns()}
          customExportClick={() => this.exportToCsv()}
          customInsightsClick={e => {
            this.insightsActionOnClick(e);
          }}
          showInsightsAction={this.showInsightsAction}
          hiddenActionButtons={['saveView', 'filter']}
          primaryActionLabel={this.primaryActionLabel}
          primaryActionClick={e => {
            this.primaryActionOnClick(e);
          }}
          primaryActionIcon={this.primaryActionIcon}
          customColumnViewClick={() => this.togglePanel('columns')}
          customFitColumnClick={() => this.fitColumns()}
          customFilterViewClick={() => this.togglePanel('filters')}
          customToggleServerClientClick={() => this.toggleServerSide()}
          customAdvancedSearchClick={() => this.showAdvancedSearch()}
          customShowGridOptionsClick={() => this.showGridOptions()}
          showAdvancedSearchFeature={this.showAdvancedSearchFeature}
          showFilterColumnFeature={this.showFilterColumnFeature}
          showHotlistButton={
            this.showHotlistButton && !(this.restrictedGridFeatures?.Hotlist && !this.userPermissions.some(permission => permission.name === this.restrictedGridFeatures.Hotlist))
          }
          hideExport={this.restrictedGridFeatures?.Export && !this.userPermissions.some(permission => permission.name === this.restrictedGridFeatures.Export)}
        ></grid-primary-bar>

        <udp-pop-over-grid-action-header isOpen={this.gridOptionsDrawerOpen} anchorElement={document.querySelector('grid-primary-bar')}>
          <stencil-icon-button-grid-action-header
            onClick={() => {
              this.togglePanel('columns');
              this.showGridOptions();
            }}
            tooltip="Show/Hide Columns"
          >
            <unity-typography variant="button">Show/Hide Columns</unity-typography>
          </stencil-icon-button-grid-action-header>
          <udp-divider size="100%"></udp-divider>
          <stencil-icon-button-grid-action-header
            onClick={() => {
              this.fitColumns();
              this.showGridOptions();
            }}
            tooltip="Fit to Column"
          >
            <unity-typography variant="button">Fit to Column</unity-typography>
          </stencil-icon-button-grid-action-header>
          <udp-divider size="0px"></udp-divider>
          <stencil-icon-button-grid-action-header
            onClick={() => {
              this.expandColumns();
              this.showGridOptions();
            }}
            tooltip="Expand Grid"
          >
            <unity-typography variant="button">Expand Grid</unity-typography>
          </stencil-icon-button-grid-action-header>
        </udp-pop-over-grid-action-header>

        <div class="second-row" style={{ display: shouldHideSecondRow ? 'none' : '' }}>
          {!this.hideBulkActions && this.rowsSelected && (
            <div class="bulk-select">
              <udp-badge content={this.selectedRowCount}>
                <udp-selector options={this.bulkActions.length > 0 ? this.formattedBulkActions : []} onOptionSelected={e => this.handleSelectedBulkOption(e)}></udp-selector>
              </udp-badge>
            </div>
          )}

          {this.loadedView.length !== 0 && (
            <div class="chip-wrapper">
              <stencil-chip
                text={this.loadedViewTitle}
                color="secondary"
                // onOnDelete={this.handleDelete as any}
                onOnDelete={this.handleViewChipDelete}
                defaultToggled={true}
                leftIcon={Star16}
              ></stencil-chip>
              <udp-divider variant="vertical" size="30px"></udp-divider>
            </div>
          )}

          {this.hotListActive && (
            <div class="chip-wrapper">
              <stencil-chip text={this.hotListActive} color="secondary" onOnDelete={this.handleRemoveHotlist} defaultToggled={true} leftIcon={List16}></stencil-chip>
              <udp-divider variant="vertical" size="30px"></udp-divider>
            </div>
          )}

          <div class="chip-section">
            {this.filterChips.length > 0 && (
              <udp-dynamic-container-with-menu
                id="filterChipsContainer"
                externalToggle={this.externalToggle}
                onUdpChipClicked={(event: CustomEvent) => this.setFilterFromChips(event.detail)}
                menuItems={this.filterChips}
              ></udp-dynamic-container-with-menu>
            )}
          </div>
          <div class="advanced-group-search-chips" id="advanced-group-search-chips">
            <div class="chip-container">
              {this.visibleChips &&
                this.visibleChips.map(filter => (
                  <stencil-chip
                    key={filter.id}
                    text={`${filter.searchFieldAlias || filter.searchField} ${filter.originalSearchOperator || filter.searchOperator} ${
                      filter.originalSearchValue || filter.searchValue
                    }`}
                    level={filter.level}
                    onOnDelete={() => this.removeFilterSelection(filter)}
                    defaultToggled={true}
                    showDelete={!filter.isInitialFilterElement}
                  />
                ))}

              {this.overflowChips && this.overflowChips.length > 0 && (
                <div>
                  {this.overflowChips.length > 0 && (
                    <stencil-icon-button icon={!this.overflowActive ? Menu16 : close16} onClick={() => (this.overflowActive = !this.overflowActive)} tooltip="Show more filters" />
                  )}
                  <udp-pop-over isOpen={this.overflowActive}>
                    <div class="overflowChip">
                      {this.overflowChips.map(filter => (
                        <stencil-chip
                          key={filter.id}
                          text={`${filter.searchFieldAlias || filter.searchField} ${filter.originalSearchOperator || filter.searchOperator} ${
                            filter.originalSearchValue || filter.searchValue
                          }`}
                          level={filter.level}
                          onOnDelete={() => this.removeFilterSelection(filter)}
                          defaultToggled={true}
                        />
                      ))}
                    </div>
                  </udp-pop-over>
                </div>
              )}
            </div>

            <div></div>
          </div>

          <div class="kpi">
            <kpi-list kpiValues={this.kpiValues}></kpi-list>
          </div>
        </div>

        {this.gridError && (
          <div>
            <upd-alert-banner type="error" message={`An error occurred with loading the grid with data.`}></upd-alert-banner>
          </div>
        )}

        <div style={{ height: '4px', backgroundColor: shouldHideSecondRow ? 'var(--accent-color)' : 'rgb(247, 245, 243)' }}>{this.linearLoading && <udp-linear-loader />}</div>

        {this.noResults && (
          <div>
            <upd-alert-banner onClick={() => this.refreshOnNoResults()} type="info" message="Sorry, no records meet your search criteria"></upd-alert-banner>
          </div>
        )}

        <div>
          {' '}
          <ag-table gridOptions={this.gridOptions} licensekey={LicenseKey} height={this.gridHeight}></ag-table>{' '}
        </div>

        <side-sheet
          sideSheetButtonLabel="Search"
          handleSideSheetButton={() => this.updateServerSideDatasource('', 'advanced search sheet')}
          title="Advanced Search"
          open={this.advancedDrawerOpen}
          toggleDrawer={() => this.primaryActionClick('advancedSearch')}
          position="right"
        >
          <div style={{ width: '1200px' }}>
            <advanced-search
              handleDelete={filter => this.removeFilterSelection(filter)}
              filterRenderList={this.filterRenderList}
              filterList={this.extendedSearchFilterList} // list that shows available filters
              handleItemClick={headerName => this.setFilterSelections(headerName)}
              onAdvancedSearchItemChange={(event: CustomEvent) => this.updateSearchObject(event.detail)}
              handleValueChanged={this.changeOperator}
              fetchFilters={() => this.getExtendedAdvancedSearchFilterList()}
            />
          </div>
        </side-sheet>

        <side-sheet widthOption="short" title="Views" open={this.viewDrawerOpen} toggleDrawer={() => this.primaryActionClick('views')} position="right">
          <div>
            <div style={{ width: '450px', marginBottom: '8px' }}>
              <hint-panel
                hint="Quickly access and manage your pre-configured table/grid views. These views are designed to help you focus on specific sets of data and streamline your workflow."
                hideHintText="Hide Hint"
              ></hint-panel>
            </div>

            <div style={{ width: '446px' }}>
              {this.viewListRowData &&
                this.viewListRowData.map(item => {
                  if (item.gridViewVisibilityTypeId === 2 || item.userId.toLowerCase() === this.userId.toLocaleLowerCase()) {
                    return (
                      <tree-list-item
                        // key={index}
                        showEditOption
                        showDeleteOption
                        label={item.name}
                        onClick={() => {
                          this.handleButtonClick(item.gridViewId);
                          this.setCurrentViewData(item.name, item.isDefault, item.gridViewVisibilityTypeId, item.gridViewId);
                        }}
                        editHandler={() => this.openViewEdit(item.gridViewId, item.name, item.isDefault, item.gridViewVisibilityTypeId)}
                        deleteHandler={() => this.openDeleteViewConfirmation(item.gridViewId)}
                        showUpdateDefaultOption={true}
                        updateDefaultHandler={() => this.updateDefaultView(item)}
                        isDefault={item.isDefault}
                        gridViewVisibilityTypeId={item.gridViewVisibilityTypeId}
                        showAdd={false}
                        showViewOption={false}
                      ></tree-list-item>
                    );
                  }
                })}
            </div>
          </div>
        </side-sheet>

        <side-sheet
          title="Search"
          sideSheetButtonLabel="Search"
          handleSideSheetButton={this.handleSideSheetButtonClick}
          open={this.customSearchDrawerOpen}
          toggleDrawer={() => this.primaryActionClick('customSearch')}
          position="right"
          buttonTransform={this.buttonTransform}
        >
          <advanced-search-grouped
            handleAddNewFilter={this.handleAddNewFilter}
            handleAddNewGroup={this.handleAddNewGroup}
            handleDelete={filter => this.removeFilterSelection(filter)}
            filterRenderList={this.filterRenderList}
            filterList={this.extendedSearchFilterList} // list that shows available filters
            handleItemClick={headerName => this.setGroupedFilterSelections(headerName)}
            onAdvancedSearchItemChange={(event: CustomEvent) => this.updateSearchObject(event.detail)}
            handleValueChanged={this.changeOperator}
            filterObjectsWithGroups={this.filterObjectsWithGroups}
            fetchFilters={() => this.getExtendedAdvancedSearchFilterList()}
            onSearchItemChanged={() => this.handleSearchItemChange()}
            updateLogicalOperator={this.updateLogicalOperator?.bind(this)}
            handleGroupItemDelete={group => this.handleGroupItemDelete(group)}
            recentSearchesOptions={this.recentSearchesOptions}
            selectRecentSearch={this.selectRecentSearch}
            fullClearSearch={this.fullClearSearch}
          />
        </side-sheet>

        <side-sheet
          disabled={this.showHotlistForm}
          widthOption="short"
          sideSheetButtonLabel="Create New Hotlist"
          handleSideSheetButton={() => this.toggleHotlistForm()}
          title="Hot Lists"
          open={this.hotlistDrawerOpen}
          toggleDrawer={() => this.primaryActionClick('hotlists')}
          position="right"
        >
          <div>
            {this.showHotlistForm && <udp-hotlist-form handleFormSubmit={this.handleSaveHotlist} handleCancelClick={() => this.toggleHotlistForm()}></udp-hotlist-form>}

            <div style={{ width: '446px' }}>
              {this.allHotlists &&
                this.allHotlists.map(item => {
                  if (item.domain === this.entityName) {
                    return (
                      <tree-list-item
                        label={item.name}
                        showViewOption={true}
                        showAdd={this.businessObjectKey ? true : false}
                        showDeleteOption={true}
                        viewHandler={() => this.handleRequestHotlist(item.hotListId)}
                        deleteHandler={() => this.confirmRequestDelete(item.hotListId)}
                        onClick={() => this.handleSetSingleHotlistItem(item.hotListId)}
                        statusValue={item.isPrivate}
                        statusClasses={{
                          true: 'default',
                          false: 'neutral',
                        }}
                        statusLabel={item.isPrivate ? 'Private' : 'Shared'}
                      ></tree-list-item>
                    );
                  }
                })}
            </div>
          </div>
        </side-sheet>

        <save-view-form-dialog
          open={this.saveDialogOpen}
          title="Save New View"
          closeDialog={() => this.showViewSave()}
          currentGridViewId={this.selectedGridId}
          currentName={this.selectedViewName}
          currentIsPrivate={this.selectedViewGridViewVisibilityTypeId}
          currentIsDefault={this.selectedViewIsDefault}
          handleFormSubmit={(name: string, isPrivate: number, isDefault: boolean) => {
            // Assuming this.columnState exists in this component's scope
            this.handleViewFormSubmit(name, isPrivate, isDefault, this.gridState);
          }}
          handleViewAndConfigurationSubmit={(name: string, isPrivate: number, isDefault: boolean) => {
            if (this.selectedGridId) {
              if (this.selectedViewGridViewVisibilityTypeId === 2) {
                this.openViewEditConfirmation(name, isPrivate, isDefault);
              } else {
                this.setCurrentViewData(name, isDefault, isPrivate);
                this.handleViewAndConfigurationsUpdate(this.selectedViewName, this.selectedViewGridViewVisibilityTypeId, this.selectedViewIsDefault, this.gridState);
              }
            }
          }}
        />

        <edit-view-form-dialog
          open={this.editDialogOpen}
          title="Edit View"
          closeDialog={() => this.closeViewEdit()}
          currentGridId={this.selectedGridId}
          currentName={this.selectedViewName}
          currentGridViewVisibilityTypeId={this.selectedViewGridViewVisibilityTypeId}
          currentIsDefault={this.selectedViewIsDefault}
          handleFormSubmit={(gridId: any, name: string, isPrivate: number, isDefault: boolean) => {
            this.handleViewUpdate(gridId, name, isPrivate, isDefault);
          }}
        />

        <div />
        <fluent-dialog
          title="Delete Hotlist"
          message="Are you sure you want to delete this hotlist?"
          actionOne={() => this.handleRequestDeleteHotlist()}
          actionTwo={() => this.toggleDeleteDialog('hotlist')}
          labelOne="Confirm"
          labelTwo="Cancel"
          open={this.confirmDialog} // Use state to control dialog visibility
        >
          <div>
            This will not delete any of the data but only the records <br />
            from the hotlist and the hotlist itself.{' '}
          </div>
        </fluent-dialog>

        <fluent-dialog
          title="Delete View"
          message="Are you sure you want to delete this view?"
          actionOne={() => this.handleDeleteView()}
          actionTwo={() => this.toggleDeleteDialog('View')}
          labelOne="Confirm"
          labelTwo="Cancel"
          open={this.confirmDialogDeleteView} // Use state to control dialog visibility
        >
          <div>
            This will not delete any of the data but only the settings <br />
            saved to display this view.{' '}
          </div>
        </fluent-dialog>

        <fluent-dialog
          title="Update View"
          message="Are you sure you want to update this view?"
          actionOne={() => this.handleViewAndConfigurationsUpdate(this.selectedViewName, this.selectedViewGridViewVisibilityTypeId, this.selectedViewIsDefault, this.gridState)}
          actionTwo={() => this.showViewEditConfirmation(false)}
          labelOne="Confirm"
          labelTwo="Cancel"
          open={this.editViewConfirmationDialogOpen}
        >
          <div>This is a public view. Any changes made to it will be seen by others when they load this view. </div>
        </fluent-dialog>
      </div>
    );
  }
}
